/* eslint-disable */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { isAdminUser, getUserVenues } from '../userUtils';
import { Star } from 'lucide-react';
const VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys'];
const ALL_VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function getWeekNumber(date) {
  const d = new Date(date);
  d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

function weekToMonthIndex(week, year) {
  const date = new Date(year, 0, 1 + (week - 1) * 7);
  return date.getMonth();
}

function weekEndDate(week, year) {
  const jan4 = new Date(year, 0, 4);
  const startOfWeek1 = new Date(jan4);
  startOfWeek1.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
  const monday = new Date(startOfWeek1);
  monday.setDate(startOfWeek1.getDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return sunday;
}

function daysInMonth(year, month) { return new Date(year, month, 0).getDate(); }

function yDomain(data, keys, padPct = 0.1) {
  const vals = data.flatMap(d => keys.map(k => d[k]).filter(v => v !== null && v !== undefined && v > 0));
  if (!vals.length) return ['auto', 'auto'];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const pad = (max - min) * padPct || max * 0.1;
  const rawMin = Math.max(0, min - pad);
  const rawMax = max + pad;
  // Snap to nearest clean step based on magnitude
  const step = rawMax > 50000 ? 10000 : rawMax > 20000 ? 5000 : rawMax > 5000 ? 2000 : rawMax > 1000 ? 1000 : rawMax > 200 ? 50 : rawMax > 50 ? 10 : 5;
  return [Math.floor(rawMin / step) * step, Math.ceil(rawMax / step) * step];
}

const currentWeek  = getWeekNumber(new Date());
const currentMonth = new Date().getMonth() + 1;

const fmt    = (n) => (n !== null && n !== undefined && n !== 0) ? `€${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
const fmtNum = (n) => (n !== null && n !== undefined && n !== 0) ? Number(n).toLocaleString('en-US') : '—';
const pct    = (a, b) => b ? ((a - b) / b * 100).toFixed(1) : null;
const fmtPct = (cost, rev) => rev ? ((cost / rev) * 100).toFixed(1) + '%' : '—';
const fmtShortfall = (actual, target) => (!actual || !target) ? null : actual - target;

const pill = (active, small = false) => ({
  padding: small ? '4px 10px' : '6px 14px',
  borderRadius: '20px', border: 'none', cursor: 'pointer',
  fontSize: small ? '12px' : '13px', fontWeight: 500,
  background: active ? '#1e293b' : '#f1f5f9',
  color: active ? '#fff' : '#64748b', transition: 'all 0.15s',
});

// ── Daily revenue breakdown ───────────────────────────────────────────────────
function DailyBreakdown({ dailyData, allDailyData, week, year, venue, isMobile, onDayClick }) {
  const DAYS_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  function getMonday(yr, wk) {
    const jan4 = new Date(yr, 0, 4);
    const startW1 = new Date(jan4);
    startW1.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
    const monday = new Date(startW1);
    monday.setDate(startW1.getDate() + (wk - 1) * 7);
    return monday;
  }

  const monday = getMonday(year, week);

  // Build day cards with date strings
  const days = DAYS_SHORT.map((day, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    const dateStr = date.getFullYear() + '-' + String(date.getMonth()+1).padStart(2,'0') + '-' + String(date.getDate()).padStart(2,'0');
    const row = dailyData.find(d => d.date === dateStr && (venue === 'Group' || d.venue === venue));
    return { day, dayIdx: i, date: `${date.getDate()} ${MONTHS[date.getMonth()]}`, dateStr, revenue: row?.total_revenue || null };
  });

  const hasAnyData = days.some(d => d.revenue);

  if (!hasAnyData) return (
    <div style={{ fontSize:'11px', color:'#94a3b8', fontStyle:'italic', padding:'4px 0 10px' }}>
      No daily data for this week — upload via weekly template to see daily breakdown.
    </div>
  );

  const weekTotal = days.reduce((s, d) => s + (d.revenue || 0), 0);

  // Top 3 per day-of-week across entire year
  // Build map: dayIdx → sorted list of revenues
  const dayOfWeekRevs = {};
  (allDailyData || dailyData).forEach(d => {
    if (venue !== 'Group' && d.venue !== venue) return;
    const dt = new Date(d.date);
    const dow = (dt.getDay() + 6) % 7; // Mon=0 … Sun=6
    if (!dayOfWeekRevs[dow]) dayOfWeekRevs[dow] = [];
    dayOfWeekRevs[dow].push(d.total_revenue || 0);
  });
  // Sort descending per day
  Object.keys(dayOfWeekRevs).forEach(k => dayOfWeekRevs[k].sort((a,b) => b - a));

  // 4-week avg per day-of-week: look back 4 completed weeks before this one
  const fourWeekAvg = {};
  DAYS_SHORT.forEach((_, i) => {
    const revs = [];
    for (let w = 1; w <= 4; w++) {
      const prevMonday = new Date(monday);
      prevMonday.setDate(monday.getDate() - w * 7);
      const prevDate = new Date(prevMonday);
      prevDate.setDate(prevMonday.getDate() + i);
      const ds = prevDate.getFullYear() + '-' + String(prevDate.getMonth()+1).padStart(2,'0') + '-' + String(prevDate.getDate()).padStart(2,'0');
      const row = (allDailyData || dailyData).find(d => d.date === ds && (venue === 'Group' || d.venue === venue));
      if (row?.total_revenue) revs.push(row.total_revenue);
    }
    fourWeekAvg[i] = revs.length > 0 ? Math.round(revs.reduce((s,v) => s+v, 0) / revs.length) : null;
  });

  return (
    <div style={{ marginBottom:'14px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px', flexWrap:'wrap' }}>
        <span style={{ fontSize:'11px', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em' }}>Daily Revenue — W{week}</span>
        <span style={{ fontSize:'12px', fontWeight:800, color:'#0f172a' }}>{fmt(weekTotal)}</span>
      </div>
      <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : undefined, gap: isMobile ? '4px' : '5px' }}>
        {days.map(({ day, dayIdx, date, dateStr, revenue }) => {
          // Top 3 rank for this day-of-week
          const topRevs = dayOfWeekRevs[dayIdx] || [];
          const rank = revenue ? topRevs.findIndex(v => v <= revenue) + 1 : null;
          const isTop3 = rank !== null && rank >= 1 && rank <= 3;
          const rankEmoji = rank === 1 ? '🍋1' : rank === 2 ? '🍋2' : rank === 3 ? '🍋3' : null;
          const avg4 = fourWeekAvg[dayIdx];
          const vsAvg = revenue && avg4 ? revenue - avg4 : null;
          return (
            <div key={day} onClick={(e) => { e.stopPropagation(); onDayClick && revenue && onDayClick({ day, date, dateStr, revenue }); }} style={{
              background: isTop3 ? '#f0fdf4' : '#fff',
              borderRadius:'8px',
              border: `1px solid ${isTop3 ? '#86efac' : '#e2e8f0'}`,
              padding: isMobile ? '6px' : '8px 8px 6px',
              flex: isMobile ? undefined : '1',
              minWidth: isMobile ? undefined : '60px',
              opacity: revenue ? 1 : 0.45,
              cursor: onDayClick && revenue ? 'pointer' : 'default',
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1px' }}>
                <div style={{ fontSize:'10px', fontWeight:700, color: isTop3 ? '#15803d' : '#64748b', textTransform:'uppercase' }}>{day}</div>
                {isTop3 && <div style={{ fontSize:'9px', fontWeight:800, color:'#15803d' }}>{rankEmoji}</div>}
              </div>
              <div style={{ fontSize:'9px', color:'#94a3b8', marginBottom:'6px' }}>{date}</div>
              {revenue ? (
                <>
                  <div style={{ fontSize:'11px', fontWeight:800, color: isTop3 ? '#15803d' : '#0f172a' }}>€{Number(revenue).toLocaleString('en-US', { maximumFractionDigits:0 })}</div>
                  {avg4 ? (
                    <div style={{ fontSize:'9px', color: vsAvg >= 0 ? '#10b981' : '#ef4444', marginTop:'3px', fontWeight:600 }}>
                      4w avg: €{Number(avg4).toLocaleString('en-US', { maximumFractionDigits:0 })} {vsAvg >= 0 ? '▲' : '▼'}
                    </div>
                  ) : (
                    <div style={{ fontSize:'9px', color:'#cbd5e1', marginTop:'3px' }}>no avg</div>
                  )}
                </>
              ) : (
                <div style={{ fontSize:'11px', color:'#cbd5e1' }}>—</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Day Modal ─────────────────────────────────────────────────────────────────
function DayModal({ day, date, dateStr, revenue, lyRevenue, fourWeekAvgRevenue, priorRevs: initialPriorRevs, venue, days, onClose }) {
  const KITCHEN_TEAMS = ['kitchen', 'kp', 'prep kitchen'];
  const isKitchenTeam = t => KITCHEN_TEAMS.includes((t || '').toLowerCase().trim());

  // Stepper
  const currentIdx = days ? days.findIndex(d => d.dateStr === dateStr) : -1;
  const prevDay    = currentIdx > 0 ? days[currentIdx - 1] : null;
  const nextDay    = currentIdx < (days?.length || 0) - 1 ? days[currentIdx + 1] : null;

  // Navigate to adjacent day — update all props via onNavigate
  const [activeDay, setActiveDay] = useState({ day, date, dateStr, revenue, lyRevenue, fourWeekAvgRevenue, priorRevs: [] });
  useEffect(() => { setActiveDay({ day, date, dateStr, revenue, lyRevenue, fourWeekAvgRevenue, priorRevs: [] }); }, [dateStr]);

  const navigateTo = (d) => setActiveDay({ day: d.day, date: d.date, dateStr: d.dateStr, revenue: d.revenue, lyRevenue: d.lyRevenue, fourWeekAvgRevenue: d.avgRev, priorRevs: d.priorRevs || [] });

  const [loading,       setLoading]       = useState(true);
  const [hours,         setHours]         = useState({ kitchen: 0, foh: 0, kitchenShifts: 0, fohShifts: 0, avgKitchen: null, avgFoh: null, avgKitchenShifts: null, avgFohShifts: null });
  const [cleaning,      setCleaning]      = useState([]);
  const [cleaningOpen,  setCleaningOpen]  = useState(false);
  const [shiftyEntries, setShiftyEntries] = useState([]);
  const [shiftyModal,   setShiftyModal]   = useState(null);
  const [events,        setEvents]        = useState([]);
  const [dayReviews,    setDayReviews]    = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setCleaning([]); setShiftyEntries([]); setEvents([]); setDayReviews([]);
      setCleaningOpen(false);

      const ds       = activeDay.dateStr;
      const dayStart = ds + 'T00:00:00';
      const dayEnd   = ds + 'T23:59:59';

      const priorDates = [];
      for (let w = 1; w <= 4; w++) {
        const d = new Date(ds + 'T12:00:00');
        d.setDate(d.getDate() - w * 7);
        priorDates.push(d.toISOString().split('T')[0]);
      }

      const [hoursRes, avgHoursRes, cleaningRes, shiftyRes, eventsRes, reviewsRes] = await Promise.all([
        supabase.from('eitje_hours').select('team, hours_worked, type').eq('venue', venue).eq('date', ds),
        supabase.from('eitje_hours').select('date, team, hours_worked, type').eq('venue', venue).in('date', priorDates),
        supabase.from('cleaning_item_completions').select('item_label, department, completed_by, completed_at').eq('venue', venue).gte('completed_at', dayStart).lte('completed_at', dayEnd).order('completed_at', { ascending: true }),
        supabase.from('shift_log').select('id,venue,shift,manager_name,submitted_at,service_notes,staff_notes,recognition_notes,upselling_notes,support_notes,stock_notes,guest_notes').eq('venue', venue).gte('submitted_at', dayStart).lte('submitted_at', dayEnd).order('submitted_at', { ascending: true }),
        supabase.from('group_events').select('id,contact_name,event_date,guest_count,event_type,status,venue').gte('event_date', ds).lte('event_date', ds).eq('status', 'confirmed'),
        supabase.from('google_reviews').select('id,reviewer_name,star_rating,review_date,comment,reply').eq('venue', venue).gte('review_date', ds).lte('review_date', ds).order('review_date', { ascending: false }),
      ]);

      // Hours aggregation
      let kitchen = 0, foh = 0, kitchenShifts = 0, fohShifts = 0;
      (hoursRes.data || []).forEach(s => {
        if ((s.type || '') !== 'gewerkte_uren') return;
        const h = parseFloat(s.hours_worked) || 0;
        if (isKitchenTeam(s.team)) { kitchen += h; kitchenShifts++; }
        else                        { foh += h;     fohShifts++;     }
      });

      const avgMap = {};
      (avgHoursRes.data || []).forEach(s => {
        if ((s.type || '') !== 'gewerkte_uren') return;
        if (!avgMap[s.date]) avgMap[s.date] = { kitchen: 0, foh: 0, kitchenShifts: 0, fohShifts: 0 };
        const h = parseFloat(s.hours_worked) || 0;
        if (isKitchenTeam(s.team)) { avgMap[s.date].kitchen += h; avgMap[s.date].kitchenShifts++; }
        else                        { avgMap[s.date].foh += h;     avgMap[s.date].fohShifts++;     }
      });
      const avgDays = Object.values(avgMap);
      const avgKitchen       = avgDays.length ? Math.round(avgDays.reduce((s,d) => s + d.kitchen, 0)       / avgDays.length * 10) / 10 : null;
      const avgFoh           = avgDays.length ? Math.round(avgDays.reduce((s,d) => s + d.foh, 0)           / avgDays.length * 10) / 10 : null;
      const avgKitchenShifts = avgDays.length ? Math.round(avgDays.reduce((s,d) => s + d.kitchenShifts, 0) / avgDays.length * 10) / 10 : null;
      const avgFohShifts     = avgDays.length ? Math.round(avgDays.reduce((s,d) => s + d.fohShifts, 0)     / avgDays.length * 10) / 10 : null;
      // Per-date total hours for Rev/hr sparkline (ordered oldest→newest)
      const totalHoursByDate = priorDates.slice().reverse().map(pd => {
        const entry = avgMap[pd];
        return entry ? Math.round((entry.kitchen + entry.foh) * 10) / 10 : null;
      });

      setHours({ kitchen: Math.round(kitchen * 10) / 10, foh: Math.round(foh * 10) / 10, kitchenShifts, fohShifts, avgKitchen, avgFoh, avgKitchenShifts, avgFohShifts, totalHoursByDate });
      setCleaning(cleaningRes.data || []);
      setShiftyEntries(shiftyRes.data || []);
      // Filter events client-side to handle venue name variations
      setEvents((eventsRes.data || []).filter(e => !e.venue || e.venue === venue));
      setDayReviews(reviewsRes.data || []);
      setLoading(false);
    }
    load();
  }, [activeDay.dateStr, venue]);

  const SHIFTY_FIELDS = [
    { key: 'service_notes', label: 'Service' }, { key: 'staff_notes', label: 'Staff' },
    { key: 'recognition_notes', label: 'Recognition' }, { key: 'upselling_notes', label: 'Upselling' },
    { key: 'support_notes', label: 'Support' }, { key: 'stock_notes', label: 'Stock' },
    { key: 'guest_notes', label: 'Guests' },
  ];

  const C2 = { cream: '#F7F6EF', charcoal: '#2F3A42', navy: '#445366', mid: '#7A8A96', border: '#D8DEE3', sky: '#BCD9E5', skyText: '#3A8FA3', green: '#7EA594', greenLight: '#EAF2EF', amber: '#E07B00', amberLight: '#FFF3E0', red: '#DC3545', redLight: '#FFF0F0' };

  const VENUE_IDENTITY_MAP = {
    'Box Jordaan':  { bg: '#EDF3FC', text: '#2563EB', border: '#BAD4F5' },
    'Box Plantage': { bg: '#C8E6D8', text: '#2D7A65', border: '#7EA594' },
    'Sassys':       { bg: '#EDE0F7', text: '#6D28A0', border: '#C4B5FD' },
    'Prep Kitchen': { bg: C2.cream,  text: C2.navy,   border: C2.border  },
  };
  const VI = VENUE_IDENTITY_MAP[venue] || { bg: C2.cream, text: C2.navy, border: C2.border };

  const cleaningByDept = cleaning.reduce((acc, c) => {
    const dept = c.department || 'General';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(c);
    return acc;
  }, {});

  const rev     = activeDay.revenue;
  const lyRev   = activeDay.lyRevenue;
  const avgRev  = activeDay.fourWeekAvgRevenue;
  const vsAvg   = rev && avgRev ? rev - avgRev : null;
  const vsLY    = rev && lyRev  ? rev - lyRev  : null;

  const stepperBtn = (enabled) => ({
    width: 28, height: 28, borderRadius: 6, border: `1px solid rgba(255,255,255,${enabled ? '0.25' : '0.1'})`,
    background: enabled ? 'rgba(255,255,255,0.12)' : 'transparent',
    cursor: enabled ? 'pointer' : 'not-allowed', fontSize: 15,
    color: enabled ? '#fff' : 'rgba(255,255,255,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  });

  const sectionHead = { fontSize: '10px', fontWeight: 700, color: C2.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' };
  const card = { background: '#fff', borderRadius: '12px', border: `1px solid ${C2.border}`, padding: '14px', marginBottom: '10px' };

  const deltaChip = (val, invert = false) => {
    if (val === null || val === undefined) return null;
    const good = invert ? val <= 0 : val >= 0;
    return (
      <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: good ? C2.greenLight : C2.redLight, color: good ? C2.green : C2.red }}>
        {val > 0 ? '+' : ''}{val > 999 || val < -999 ? fmt(val) : val}h {good ? '▲' : '▼'}
      </span>
    );
  };

  const activeIdx   = days ? days.findIndex(d => d.dateStr === activeDay.dateStr) : -1;
  const activePrev  = activeIdx > 0 ? days[activeIdx - 1] : null;
  const activeNext  = activeIdx < (days?.length || 0) - 1 ? days[activeIdx + 1] : null;

  return (
    <>
      {/* Shifty inner modal */}
      {shiftyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.6)', zIndex: 10002, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px 16px', overflowY: 'auto' }}
          onClick={() => setShiftyModal(null)}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '500px', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', overflow: 'hidden', marginBottom: '16px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: C2.navy, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '15px', color: '#fff' }}>{shiftyModal.manager_name || 'Shift Report'}{shiftyModal.shift ? ` · ${shiftyModal.shift}` : ''}</div>
                <div style={{ fontSize: '12px', color: C2.sky, marginTop: '2px' }}>{shiftyModal.venue}{shiftyModal.submitted_at ? ` · ${new Date(shiftyModal.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
              </div>
              <button onClick={() => setShiftyModal(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', padding: '6px 9px' }}>✕</button>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px', background: C2.cream }}>
              {SHIFTY_FIELDS.filter(f => shiftyModal[f.key]?.trim()).length === 0
                ? <p style={{ color: C2.mid, fontSize: '13px', margin: 0 }}>No notes were filled in for this shift.</p>
                : SHIFTY_FIELDS.filter(f => shiftyModal[f.key]?.trim()).map(f => (
                    <div key={f.key}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: C2.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{f.label}</div>
                      <div style={{ fontSize: '13px', color: C2.charcoal, lineHeight: 1.6, background: '#fff', borderRadius: '8px', padding: '9px 12px', border: `1px solid ${C2.border}` }}>{shiftyModal[f.key]}</div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      )}

      {/* DayModal backdrop — always centred */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
        <div style={{ background: C2.cream, borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}>

          {/* Header */}
          <div style={{ background: C2.navy, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <button onClick={() => activePrev && navigateTo(activePrev)} disabled={!activePrev} style={stepperBtn(!!activePrev)}>‹</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ marginBottom: '4px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 9px', borderRadius: '20px', background: VI.bg, color: VI.text, border: `1px solid ${VI.border}` }}>{venue}</span>
              </div>
              <div style={{ fontWeight: 900, fontSize: '17px', color: '#fff', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {new Date(activeDay.dateStr + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long' })} · {activeDay.date}
              </div>
            </div>
            <button onClick={() => activeNext && navigateTo(activeNext)} disabled={!activeNext} style={stepperBtn(!!activeNext)}>›</button>
          </div>

          <div style={{ overflowY: 'auto', padding: '14px', flex: 1 }}>

            {/* ── Row 1: Revenue | Rev/hr ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              {/* Revenue card */}
              {(() => {
                return (
                  <div style={{ ...card, marginBottom: 0, borderTop: `3px solid ${VI.border}`, paddingTop: '11px' }}>
                    <div style={{ ...sectionHead, color: VI.text }}>Revenue</div>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: C2.charcoal, lineHeight: 1.1, marginBottom: '6px' }}>{fmt(rev)}</div>
                    <div style={{ borderTop: `1px solid ${C2.border}`, paddingTop: '7px', display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: C2.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>4w avg</div>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: C2.charcoal }}>{fmt(avgRev)}</div>
                        {vsAvg !== null && <div style={{ fontSize: '12px', fontWeight: 700, color: vsAvg >= 0 ? C2.green : C2.red }}>{vsAvg >= 0 ? '+' : ''}{fmt(vsAvg)}</div>}
                      </div>
                      {lyRev !== null && (
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: C2.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>LY</div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: C2.charcoal }}>{fmt(lyRev)}</div>
                          {vsLY !== null && <div style={{ fontSize: '12px', fontWeight: 700, color: vsLY >= 0 ? C2.green : C2.red }}>{vsLY >= 0 ? '+' : ''}{fmt(vsLY)}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Rev/hr card */}
              {(() => {
                const totalHrs   = Math.round((hours.kitchen + hours.foh) * 10) / 10;
                const revHr      = rev && totalHrs > 0 ? Math.round(rev / totalHrs) : null;
                const priorRevsList = activeDay.priorRevs || initialPriorRevs || [];
                const thbd = hours.totalHoursByDate || [];
                const avgRevHrVals = priorRevsList.slice(-4).reverse().map((r, i) => {
                  const h = thbd[i]; return (r && h && h > 0) ? Math.round(r / h) : null;
                }).filter(Boolean);
                const avgRevHr = avgRevHrVals.length ? Math.round(avgRevHrVals.reduce((s,v) => s+v,0) / avgRevHrVals.length) : null;
                const vsRevHrAvg = revHr && avgRevHr ? revHr - avgRevHr : null;
                return (
                  <div style={{ ...card, marginBottom: 0, borderTop: '3px solid #C4B5FD', paddingTop: '11px' }}>
                    <div style={{ ...sectionHead, color: '#6D28A0' }}>Rev / Hr</div>
                    <div style={{ fontSize: '16px', fontWeight: 800, color: C2.charcoal, lineHeight: 1.1, marginBottom: '6px' }}>{revHr ? fmt(revHr) : '—'}</div>
                    <div style={{ borderTop: `1px solid ${C2.border}`, paddingTop: '7px', display: 'flex', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: C2.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>4w avg</div>
                        {avgRevHr !== null && <div style={{ fontSize: '12px', fontWeight: 700, color: C2.charcoal }}>{fmt(avgRevHr)}</div>}
                        {vsRevHrAvg !== null && <div style={{ fontSize: '12px', fontWeight: 700, color: vsRevHrAvg >= 0 ? C2.green : C2.red }}>{vsRevHrAvg >= 0 ? '+' : ''}{fmt(vsRevHrAvg)}</div>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: C2.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Total hrs</div>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: C2.charcoal }}>{totalHrs > 0 ? totalHrs + 'h' : '—'}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ── Row 2: FOH Hrs | Kitchen Hrs ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              {[
                { label: 'FOH Hrs',     hrs: hours.foh,     shifts: hours.fohShifts,     avgHrs: hours.avgFoh,     avgShifts: hours.avgFohShifts,     accent: '#BCD9E5', accentBg: '#EAF4F8', accentText: '#3A8FA3' },
                { label: 'Kitchen Hrs', hrs: hours.kitchen, shifts: hours.kitchenShifts, avgHrs: hours.avgKitchen, avgShifts: hours.avgKitchenShifts, accent: '#F2B8CB', accentBg: '#FDF0F5', accentText: '#C4607A' },
              ].map(({ label, hrs, shifts, avgHrs, avgShifts, accent, accentBg, accentText }) => {
                const deltaHrs    = (hrs > 0 && avgHrs)       ? Math.round((hrs    - avgHrs)    * 10) / 10 : null;
                const deltaShifts = (shifts > 0 && avgShifts) ? Math.round((shifts - avgShifts) * 10) / 10 : null;
                const goodHrs     = deltaHrs    !== null ? deltaHrs    <= 0 : null;
                const goodShifts  = deltaShifts !== null ? deltaShifts <= 0 : null;
                return (
                  <div key={label} style={{ ...card, marginBottom: 0, borderTop: `3px solid ${accent}` , paddingTop: '11px' }}>
                    <div style={{ ...sectionHead, color: accentText }}>{label}</div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: 800, color: C2.charcoal, lineHeight: 1.1 }}>{hrs > 0 ? (Math.round(hrs * 10) / 10) + 'h' : '—'}</div>
                        <div style={{ fontSize: '11px', color: C2.mid, marginTop: '2px' }}>hours</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: 800, color: C2.charcoal, lineHeight: 1.1 }}>{shifts}</div>
                        <div style={{ fontSize: '11px', color: C2.mid, marginTop: '2px' }}>shifts</div>
                      </div>
                    </div>
                    {avgHrs !== null && (
                      <div style={{ borderTop: `1px solid ${C2.border}`, paddingTop: '8px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: C2.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>4w avg</div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: C2.charcoal }}>{(Math.round(avgHrs * 10) / 10)}h</div>
                            {deltaHrs !== null && <div style={{ fontSize: '12px', fontWeight: 700, color: goodHrs ? C2.green : C2.red }}>{deltaHrs > 0 ? '+' : ''}{deltaHrs}h</div>}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: C2.charcoal }}>{avgShifts !== null ? (Math.round(avgShifts * 10) / 10) : '—'} shifts</div>
                            {deltaShifts !== null && <div style={{ fontSize: '12px', fontWeight: 700, color: goodShifts ? C2.green : C2.red }}>{deltaShifts > 0 ? '+' : ''}{deltaShifts}</div>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Cleaning ── */}
            <div style={card}>
              <div style={sectionHead}>Cleaning</div>
              {loading ? <div style={{ fontSize: '13px', color: C2.mid }}>Loading…</div>
              : cleaning.length === 0 ? (
                <div style={{ fontSize: '13px', color: C2.mid }}>No cleaning tasks recorded.</div>
              ) : (
                <>
                  <button onClick={() => setCleaningOpen(o => !o)} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: C2.charcoal }}>🧹 {cleaning.length} task{cleaning.length !== 1 ? 's' : ''} completed</span>
                    <span style={{ fontSize: '11px', color: C2.mid }}>{cleaningOpen ? '▲ hide' : '▼ show'}</span>
                  </button>
                  {cleaningOpen && (
                    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {Object.entries(cleaningByDept).map(([dept, items]) => (
                        <div key={dept}>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: C2.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>{dept}</div>
                          {items.map((c, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < items.length - 1 ? `1px solid ${C2.border}` : 'none' }}>
                              <span style={{ fontSize: '12px', color: C2.charcoal }}>{c.item_label}</span>
                              <span style={{ fontSize: '11px', color: C2.mid, whiteSpace: 'nowrap', marginLeft: '8px' }}>
                                {c.completed_by}{c.completed_at ? ` · ${new Date(c.completed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Shift Reports ── */}
            <div style={card}>
              <div style={sectionHead}>Shift Reports</div>
              {loading ? <div style={{ fontSize: '13px', color: C2.mid }}>Loading…</div>
              : shiftyEntries.length === 0 ? (
                <div style={{ fontSize: '13px', color: C2.mid }}>No shift reports filed.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {shiftyEntries.map(entry => (
                    <button key={entry.id} onClick={() => setShiftyModal(entry)} style={{ width: '100%', background: C2.cream, border: `1px solid ${C2.border}`, borderRadius: '8px', padding: '9px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: C2.charcoal }}>{entry.manager_name || 'Shift Report'}{entry.shift ? ` · ${entry.shift}` : ''}</span>
                      <span style={{ fontSize: '11px', color: C2.mid }}>{entry.submitted_at ? new Date(entry.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''} ›</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Events ── */}
            <div style={card}>
              <div style={sectionHead}>Events</div>
              {loading ? <div style={{ fontSize: '13px', color: C2.mid }}>Loading…</div>
              : events.length === 0 ? (
                <div style={{ fontSize: '13px', color: C2.mid }}>No events.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {events.map(ev => (
                    <div key={ev.id} style={{ background: C2.cream, border: `1px solid ${C2.border}`, borderRadius: '8px', padding: '9px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: C2.charcoal }}>{ev.contact_name || 'Group Event'}</span>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {ev.event_type && <span style={{ fontSize: '11px', color: C2.mid }}>{ev.event_type}</span>}
                        {ev.guest_count && <span style={{ fontSize: '11px', fontWeight: 700, color: C2.charcoal, background: C2.sky, borderRadius: '10px', padding: '2px 8px' }}>{ev.guest_count}pp</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Reviews ── */}
            {!loading && dayReviews.length > 0 && (
              <div style={{ ...card, marginBottom: 0 }}>
                <div style={{ ...sectionHead, display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                  <Star size={11} color='#E4AC49' fill='#F7EC71' strokeWidth={1.5} />
                  <span>Reviews</span>
                </div>
                {/* Star breakdown pills */}
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {[5,4,3,2,1].map(s => {
                    const n = dayReviews.filter(r => r.star_rating === s).length;
                    if (!n) return null;
                    return (
                      <span key={s} style={{ fontSize: '11px', fontWeight: 700, padding: '2px 9px', borderRadius: '20px', background: '#FEFCE8', color: '#7A8A96', border: '1px solid #F7EC71' }}>
                        {s}★ ×{n}
                      </span>
                    );
                  })}
                </div>
                {/* Individual reviews */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {dayReviews.map(r => (
                    <div key={r.id} style={{ background: C2.cream, borderRadius: '8px', padding: '9px 11px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: C2.charcoal, marginBottom: '3px' }}>
                        {r.reviewer_name || 'Anonymous'}
                      </div>
                      {r.comment && (
                        <div style={{ fontSize: '11px', color: C2.charcoal, lineHeight: 1.5 }}>
                          {r.comment.trim().length > 300 ? r.comment.trim().slice(0, 300) + '…' : r.comment.trim()}
                        </div>
                      )}
                      {r.reply && (
                        <div style={{ marginTop: '5px' }}>
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '20px', background: '#EAF2EF', color: '#7EA594', border: '1px solid #7EA59433' }}>✓ Replied</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loading && (
              <div style={{ textAlign: 'center', padding: '20px', color: C2.mid, fontSize: '13px' }}>Loading…</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Week Modal ────────────────────────────────────────────────────────────────
function WeekModal({ r, lyRow, dailyData, dailyDataLY, enriched, enrichedLY, venue, year,
                     comments, isManager,
                     newComment, setNewComment, postingComment, onPostComment,
                     views, onView, currentUser, onNavigate, onClose }) {
  const isMobile = useIsMobile();
  const [modalTab, setModalTab] = useState('overview');
  const [dayModal, setDayModal] = useState(null); // { day, date, dateStr, revenue }
  const [weekReviews, setWeekReviews] = useState([]);
  const isGroup = venue === 'Group';

  // Week stepper — sorted by week number
  const sortedWeeks = [...enriched].sort((a, b) => a.week - b.week);
  const currentIdx  = sortedWeeks.findIndex(x => x.week === r.week);
  const prevWeek    = currentIdx > 0 ? sortedWeeks[currentIdx - 1] : null;
  const nextWeek    = currentIdx < sortedWeeks.length - 1 ? sortedWeeks[currentIdx + 1] : null;

  // Log view once per user on mount — ref prevents re-fire on state updates
  const viewLoggedRef = useRef(false);
  useEffect(() => {
    if (r?.id && currentUser?.email && !viewLoggedRef.current) {
      viewLoggedRef.current = true;
      onView(r.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch reviews for this week
  useEffect(() => {
    if (isGroup || !venue || !r?.week || !year) return;
    async function loadWeekReviews() {
      // Use same ISO week logic as weekEndDate helper at top of file
      const jan4 = new Date(year, 0, 4);
      const startOfWeek1 = new Date(jan4);
      startOfWeek1.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
      // Monday of the target week
      const monday = new Date(startOfWeek1);
      monday.setDate(startOfWeek1.getDate() + (r.week - 1) * 7);
      // Sunday of the target week
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      // Local date strings — never use toISOString (UTC offset corrupts NL dates)
      const fmtLocal = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const { data } = await supabase
        .from('google_reviews')
        .select('id,reviewer_name,star_rating,review_date,comment')
        .eq('venue', venue)
        .gte('review_date', fmtLocal(monday))
        .lte('review_date', fmtLocal(sunday))
        .order('review_date', { ascending: false });
      setWeekReviews(data || []);
    }
    loadWeekReviews();
  }, [venue, r?.week, year, isGroup]);

  const existingComments = comments.filter(c => c.result_id === r.id);
  const latestComment    = existingComments[existingComments.length - 1];
  const parsedComment    = (() => {
    if (!latestComment) return null;
    try { return JSON.parse(latestComment.comment); } catch { return { notes: latestComment.comment }; }
  })();

  const [highlights,  setHighlights]  = useState(parsedComment?.highlights  || '');
  const [watchpoints, setWatchpoints] = useState(parsedComment?.watchpoints || '');
  const [context,     setContext]     = useState(parsedComment?.context      || '');
  const [freeNotes,   setFreeNotes]   = useState(parsedComment?.notes        || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved,  setNotesSaved]  = useState(false);

  const lyPct     = lyRow ? parseFloat(pct(r.total_revenue, lyRow.total_revenue)) : null;
  const lyRevDiff = lyRow ? Math.round(r.total_revenue - lyRow.total_revenue) : null;
  const revHr     = r.total_hours > 0 ? Math.round(r.total_revenue / r.total_hours) : null;
  const lyRevHr   = lyRow?.total_hours > 0 ? Math.round(lyRow.total_revenue / lyRow.total_hours) : null;

  // KPI strip — 3 metrics
  const kpis = [
    {
      label: 'Revenue',
      value: fmt(r.total_revenue),
      delta: lyRevDiff !== null ? (lyRevDiff >= 0 ? '+' : '') + fmt(lyRevDiff) : null,
      good:  lyRevDiff !== null ? lyRevDiff >= 0 : null,
    },
    {
      label: 'Rev / Hr',
      value: revHr ? fmt(revHr) : '—',
      delta: revHr && lyRevHr ? (revHr - lyRevHr >= 0 ? '+' : '') + fmt(revHr - lyRevHr) : null,
      good:  revHr && lyRevHr ? revHr >= lyRevHr : null,
    },
    {
      label: 'Hours',
      value: r.total_hours ? Math.round(r.total_hours) + 'h' : '—',
      sub: (r.kitchen_hours > 0 || r.foh_hours > 0)
        ? `K:${Math.round(r.kitchen_hours||0)}h  F:${Math.round(r.foh_hours||0)}h`
        : null,
      delta: r.total_hours && lyRow?.total_hours
        ? (r.total_hours - lyRow.total_hours >= 0 ? '+' : '') + Math.round(r.total_hours - lyRow.total_hours) + 'h'
        : null,
      good: r.total_hours && lyRow?.total_hours ? r.total_hours <= lyRow.total_hours : null,
    },
  ];

  // Daily vs LY chart data
  const DAYS_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  function getMondayOfWeek(yr, wk) {
    const jan4 = new Date(yr, 0, 4);
    const startW1 = new Date(jan4);
    startW1.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
    const monday = new Date(startW1);
    monday.setDate(startW1.getDate() + (wk - 1) * 7);
    return monday;
  }
  const toDateStr = d => d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  const monday   = getMondayOfWeek(year, r.week);
  const mondayLY = getMondayOfWeek(year - 1, r.week);

  const dailyVsLY = DAYS_SHORT.map((day, i) => {
    const dateCY = new Date(monday); dateCY.setDate(monday.getDate() + i);
    const dateLY = new Date(mondayLY); dateLY.setDate(mondayLY.getDate() + i);
    const rowCY = dailyData.find(d => d.date === toDateStr(dateCY) && (venue === 'Group' || d.venue === venue));
    const lySource = (dailyDataLY && dailyDataLY.length > 0) ? dailyDataLY : dailyData;
    const rowLY = lySource.find(d => d.date === toDateStr(dateLY) && (venue === 'Group' || d.venue === venue));
    return {
      day,
      'This Week': rowCY?.total_revenue || null,
      'Last Year': rowLY?.total_revenue || null,
    };
  });
  // Build full days array for DayModal stepper + LY lookup
  const daysForModal = DAYS_SHORT.map((d, i) => {
    const dateCY = new Date(monday); dateCY.setDate(monday.getDate() + i);
    const dateLY = new Date(mondayLY); dateLY.setDate(mondayLY.getDate() + i);
    const rowCY  = dailyData.find(x => x.date === toDateStr(dateCY) && (venue === 'Group' || x.venue === venue));
    const lySource = (dailyDataLY && dailyDataLY.length > 0) ? dailyDataLY : dailyData;
    const rowLY  = lySource.find(x => x.date === toDateStr(dateLY) && (venue === 'Group' || x.venue === venue));
    const ds = toDateStr(dateCY);
    // Compute 4w avg revenue for this day
    const priorRevs = [];
    for (let w = 1; w <= 4; w++) {
      const pd = new Date(dateCY); pd.setDate(dateCY.getDate() - w * 7);
      const pds = toDateStr(pd);
      const pr = dailyData.find(x => x.date === pds && x.venue === venue);
      if (pr?.total_revenue) priorRevs.push(pr.total_revenue);
    }
    const avgRev = priorRevs.length ? Math.round(priorRevs.reduce((s,v) => s+v,0) / priorRevs.length) : null;
    return { day: d, date: `${dateCY.getDate()} ${MONTHS[dateCY.getMonth()]}`, dateStr: ds, revenue: rowCY?.total_revenue || null, lyRevenue: rowLY?.total_revenue || null, avgRev, priorRevs };
  });

  const hasDailyChart = dailyVsLY.some(d => d['This Week'] || d['Last Year']);

  const handleSaveNotes = async () => {
    if (!isManager) return;
    setSavingNotes(true);
    const payload = JSON.stringify({ highlights, watchpoints, context, notes: freeNotes });
    setNewComment(payload);
    await onPostComment(r.id, payload);
    setSavingNotes(false);
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 3000);
  };

  const tabs = [
    { key:'overview', label:'Overview' },
    ...(!isGroup ? [{ key:'notes', label:'Notes' }] : []),
  ];

  const inputStyle = {
    width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0',
    borderRadius:'8px', fontSize:'13px', outline:'none', resize:'vertical',
    fontFamily:'inherit', lineHeight:1.5, color:'#0f172a',
    background:'#fafafa', boxSizing:'border-box',
  };

  const weekViews = views.filter(v => v.result_id === r.id);

  return (
    <>
    <div style={{
      position:'fixed', inset:0, zIndex:9999,
      background:'rgba(15,23,42,0.5)', backdropFilter:'blur(4px)',
      display:'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent:'center',
      padding: isMobile ? '0' : '20px',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background:'#fff', borderRadius: isMobile ? '16px 16px 0 0' : '16px',
        width:'100%', maxWidth:'820px', maxHeight:'92vh',
        overflow:'hidden', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{ padding: isMobile ? '16px 16px 0' : '20px 24px 0', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                {/* Prev/next stepper */}
                <button
                  onClick={() => prevWeek && onNavigate(prevWeek)}
                  disabled={!prevWeek}
                  style={{ width:26, height:26, borderRadius:6, border:'1px solid #e2e8f0', background: prevWeek ? '#fff' : '#f8fafc', cursor: prevWeek ? 'pointer' : 'not-allowed', fontSize:14, color: prevWeek ? '#64748b' : '#cbd5e1', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.1s' }}
                  title={prevWeek ? `W${prevWeek.week}` : ''}
                >‹</button>
                <h3 style={{ margin:0, fontSize: isMobile ? '17px' : '20px', fontWeight:800, color:'#0f172a' }}>Week {r.week} · {year}</h3>
                <button
                  onClick={() => nextWeek && onNavigate(nextWeek)}
                  disabled={!nextWeek}
                  style={{ width:26, height:26, borderRadius:6, border:'1px solid #e2e8f0', background: nextWeek ? '#fff' : '#f8fafc', cursor: nextWeek ? 'pointer' : 'not-allowed', fontSize:14, color: nextWeek ? '#64748b' : '#cbd5e1', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.1s' }}
                  title={nextWeek ? `W${nextWeek.week}` : ''}
                >›</button>
                {lyPct !== null && (
                  <span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:700, background: lyPct >= 0 ? '#f0fdf4' : '#fef2f2', color: lyPct >= 0 ? '#10b981' : '#ef4444', border: `1px solid ${lyPct >= 0 ? '#bbf7d0' : '#fecaca'}` }}>
                    {lyPct >= 0 ? '▲' : '▼'} {Math.abs(lyPct)}% vs LY
                  </span>
                )}
                {lyRevDiff !== null && (
                  <span style={{ fontSize:'13px', color: lyRevDiff >= 0 ? '#10b981' : '#ef4444', fontWeight:600 }}>
                    {lyRevDiff >= 0 ? '+' : ''}{fmt(lyRevDiff)}
                  </span>
                )}
              </div>
              <div style={{ fontSize:'13px', color:'#64748b', marginTop:'2px' }}>{venue}</div>
            </div>
            <button onClick={onClose} style={{ background:'#f1f5f9', border:'none', borderRadius:'8px', width:32, height:32, cursor:'pointer', fontSize:'18px', color:'#64748b', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginLeft:'8px' }}>×</button>
          </div>

          {/* KPI strip — 3 cols */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1px', background:'#f1f5f9', borderRadius:'10px', overflow:'hidden', marginBottom:'14px' }}>
            {kpis.map(k => (
              <div key={k.label} style={{ background:'#fff', padding:'10px 14px' }}>
                <div style={{ fontSize:'10px', color:'#94a3b8', textTransform:'uppercase', fontWeight:700, letterSpacing:'0.04em', marginBottom:'3px' }}>{k.label}</div>
                <div style={{ fontSize: isMobile ? '15px' : '17px', fontWeight:800, color:'#0f172a', marginBottom:'2px' }}>{k.value}</div>
                {k.sub && <div style={{ fontSize:'10px', color:'#64748b', marginBottom:'2px', fontWeight:500 }}>{k.sub}</div>}
                {k.delta
                  ? <div style={{ fontSize:'11px', fontWeight:700, color: k.good ? '#10b981' : '#ef4444' }}>{k.delta} vs LY {k.good ? '▲' : '▼'}</div>
                  : <div style={{ fontSize:'11px', color:'#cbd5e1' }}>No LY data</div>
                }
              </div>
            ))}
          </div>

          <div style={{ display:'flex' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setModalTab(t.key)} style={{ padding:'8px 16px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight: modalTab === t.key ? 700 : 400, color: modalTab === t.key ? '#0f172a' : '#64748b', background:'transparent', borderBottom: modalTab === t.key ? '2px solid #0f172a' : '2px solid transparent', borderRadius:0, marginBottom:'-1px' }}>{t.label}</button>
            ))}
          </div>
        </div>

        <div style={{ overflowY:'auto', padding: isMobile ? '16px' : '20px 24px', flex:1 }}>
          {modalTab === 'overview' && (
            <div>
              {/* Daily breakdown cards */}
              <div style={{ marginBottom:'14px' }}>
                <DailyBreakdown dailyData={dailyData} allDailyData={dailyData} week={r.week} year={r.year} venue={venue} isMobile={isMobile} onDayClick={!isGroup ? setDayModal : undefined}/>
              </div>

              {/* Daily vs LY grouped bar chart */}
              {hasDailyChart && (
                <div style={{ background:'#f8fafc', borderRadius:'10px', padding:'14px', border:'1px solid #e2e8f0', marginBottom:'14px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px' }}>
                    <div style={{ fontSize:'12px', fontWeight:700, color:'#0f172a' }}>Daily Revenue — W{r.week} vs LY</div>
                    <div style={{ display:'flex', gap:'10px', marginLeft:'auto' }}>
                      {[['#3A8FA3','This Week'],['#BCD9E5','Last Year']].map(([c,l])=>(
                        <span key={l} style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'10px', color:'#64748b' }}>
                          <span style={{ width:10, height:10, borderRadius:'2px', background:c, display:'inline-block' }}/>{l}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={dailyVsLY} barGap={3} barCategoryGap="28%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" vertical={false}/>
                      <XAxis dataKey="day" tick={{fontSize:10, fill:'#94a3b8'}} axisLine={false} tickLine={false}/>
                      <YAxis tick={{fontSize:10, fill:'#94a3b8'}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} width={40} axisLine={false} tickLine={false}/>
                      <Tooltip formatter={fmt} contentStyle={{borderRadius:'8px',border:'1px solid #e2e8f0',fontSize:'12px'}}/>
                      <Bar dataKey="This Week" fill="#3A8FA3" radius={[3,3,0,0]}/>
                      <Bar dataKey="Last Year" fill="#BCD9E5" radius={[3,3,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Revenue split — dinner from manual weekly input, brunch = total (Eitje) − dinner */}
              {venue !== 'Sassys' && venue !== 'Group' && r.dinner_revenue > 0 && r.total_revenue > 0 && (() => {
                const total  = r.total_revenue;
                const dinner = r.dinner_revenue || 0;
                const brunch = Math.max(0, total - dinner);
                const bp = total > 0 ? Math.round((brunch / total) * 100) : 0;
                const dp = 100 - bp;
                return (
                  <div style={{ background:'#f8fafc', borderRadius:'10px', padding:'14px', border:'1px solid #e2e8f0', marginBottom:'14px' }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'10px' }}>Revenue Split</div>
                    <div style={{ display:'flex', borderRadius:'6px', overflow:'hidden', height:'8px', marginBottom:'10px' }}>
                      <div style={{ width:`${bp}%`, background:'#D4A84B' }}/>
                      <div style={{ width:`${dp}%`, background:'#7EA8C4' }}/>
                    </div>
                    {[['#D4A84B','Brunch',brunch,bp],['#7EA8C4','Dinner',dinner,dp]].map(([c,l,v,p])=>(
                      <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                          <span style={{ width:8, height:8, borderRadius:'50%', background:c, display:'inline-block', flexShrink:0 }}/>
                          <span style={{ fontSize:'11px', color:'#64748b' }}>{l}</span>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <span style={{ fontSize:'12px', fontWeight:700, color:'#0f172a' }}>{fmt(v)}</span>
                          <span style={{ fontSize:'10px', color:'#94a3b8', marginLeft:'4px' }}>{p}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Covers + Rev/Cover row */}
              {(() => {
                const rpc = r.total_covers > 0 ? Math.round((r._avg_rev_source ?? r.total_revenue) / r.total_covers) : null;
                const lyRpc = lyRow?.total_covers > 0 ? Math.round((lyRow._avg_rev_source ?? lyRow.total_revenue) / lyRow.total_covers) : null;
                const rpcDelta = rpc && lyRpc ? rpc - lyRpc : null;
                const coversDelta = r.total_covers && lyRow?.total_covers ? r.total_covers - lyRow.total_covers : null;
                const items = [
                  {
                    label: 'Total Covers',
                    value: r.total_covers > 0 ? fmtNum(r.total_covers) : '—',
                    delta: coversDelta !== null ? (coversDelta >= 0 ? '+' : '') + fmtNum(coversDelta) + ' vs LY' : null,
                    good:  coversDelta !== null ? coversDelta >= 0 : null,
                  },
                  {
                    label: 'Rev / Cover',
                    value: rpc ? fmt(rpc) : '—',
                    delta: rpcDelta !== null ? (rpcDelta >= 0 ? '+' : '') + fmt(rpcDelta) + ' vs LY' : null,
                    good:  rpcDelta !== null ? rpcDelta >= 0 : null,
                  },
                ];
                return (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1px', background:'#f1f5f9', borderRadius:'10px', overflow:'hidden', marginBottom:'14px' }}>
                    {items.map(k => (
                      <div key={k.label} style={{ background:'#fff', padding:'10px 14px' }}>
                        <div style={{ fontSize:'10px', color:'#94a3b8', textTransform:'uppercase', fontWeight:700, letterSpacing:'0.04em', marginBottom:'3px' }}>{k.label}</div>
                        <div style={{ fontSize: isMobile ? '15px' : '17px', fontWeight:800, color:'#0f172a', marginBottom:'2px' }}>{k.value}</div>
                        {k.delta
                          ? <div style={{ fontSize:'11px', fontWeight:700, color: k.good ? '#10b981' : '#ef4444' }}>{k.delta} {k.good ? '▲' : '▼'}</div>
                          : <div style={{ fontSize:'11px', color:'#cbd5e1' }}>No LY data</div>
                        }
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Weekly reviews summary */}
              {!isGroup && weekReviews.length > 0 && (() => {
                const totalCount = weekReviews.length;
                const avgRating  = (Math.floor(weekReviews.reduce((s,r) => s + r.star_rating, 0) / totalCount * 10) / 10).toFixed(1);
                const fiveCount  = weekReviews.filter(r => r.star_rating === 5).length;
                return (
                  <div style={{ background:'#FEFCE8', borderRadius:'10px', padding:'12px 14px', border:'1px solid #F7EC71', marginBottom:'14px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'10px' }}>
                      <Star size={12} color='#E4AC49' fill='#F7EC71' strokeWidth={1.5} />
                      <span style={{ fontSize:'11px', fontWeight:800, color:'#7A8A96', textTransform:'uppercase', letterSpacing:'0.05em' }}>Reviews this week</span>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'10px' }}>
                      {[
                        { label: 'Total',   value: totalCount },
                        { label: 'Avg',     value: avgRating + '★' },
                        { label: '5★',      value: fiveCount },
                      ].map(k => (
                        <div key={k.label} style={{ background:'#fff', borderRadius:'7px', padding:'8px 10px', textAlign:'center', border:'1px solid #F7EC7166' }}>
                          <div style={{ fontSize:'10px', fontWeight:700, color:'#7A8A96', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'2px' }}>{k.label}</div>
                          <div style={{ fontSize:'16px', fontWeight:900, color:'#2F3A42' }}>{k.value}</div>
                        </div>
                      ))}
                    </div>
                    {/* Show up to 3 most recent */}
                    <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                      {weekReviews.slice(0, 3).map(rev => (
                        <div key={rev.id} style={{ background:'#fff', borderRadius:'7px', padding:'7px 10px', border:'1px solid #F7EC7166' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'5px', marginBottom:'2px' }}>
                            {[1,2,3,4,5].map(i => <Star key={i} size={9} fill={i <= rev.star_rating ? '#F7EC71' : 'none'} stroke={i <= rev.star_rating ? '#E4AC49' : '#D8DEE3'} strokeWidth={1.5} />)}
                            <span style={{ fontSize:'10px', fontWeight:700, color:'#2F3A42' }}>{rev.reviewer_name || 'Anonymous'}</span>
                          </div>
                          {rev.comment && (
                            <div style={{ fontSize:'10px', color:'#7A8A96', lineHeight:1.4 }}>
                              {rev.comment.trim().slice(0, 120)}{rev.comment.trim().length > 120 ? '…' : ''}
                            </div>
                          )}
                        </div>
                      ))}
                      {totalCount > 3 && (
                        <div style={{ fontSize:'10px', color:'#7A8A96', textAlign:'center', paddingTop:'2px' }}>+{totalCount - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Viewed by */}
              <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:'12px', marginTop:'4px' }}>
                <div style={{ fontSize:'10px', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'8px' }}>Viewed by</div>
                {weekViews.length === 0
                  ? <span style={{ fontSize:'12px', color:'#cbd5e1' }}>No views yet</span>
                  : (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                      {weekViews.map(v => (
                        <span key={v.id} style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'3px 10px', borderRadius:'20px', background:'#f8fafc', border:'1px solid #e2e8f0', fontSize:'11px', color:'#374151' }}>
                          <span style={{ width:6, height:6, borderRadius:'50%', background:'#10b981', display:'inline-block' }}/>
                          {v.user_name}
                          <span style={{ color:'#94a3b8' }}>· {new Date(v.viewed_at).toLocaleDateString('en-GB')}</span>
                        </span>
                      ))}
                    </div>
                  )
                }
              </div>
            </div>
          )}

          {modalTab === 'notes' && !isGroup && (
            <div>
              {isManager && (
                <div>
                  <div style={{ fontSize:'12px', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'14px' }}>Week Notes</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'12px', marginBottom:'16px' }}>
                    {[
                      { icon:'⭐', label:'Highlights', sub:'What went well this week?', val:highlights, set:setHighlights, rows:3 },
                      { icon:'⚠️', label:'Watch Points', sub:'Staff, ops, complaints, anything to flag?', val:watchpoints, set:setWatchpoints, rows:3 },
                      { icon:'🌤', label:'Context', sub:'Weather, local events, construction?', val:context, set:setContext, rows:2 },
                      { icon:'💬', label:'Free Notes', sub:'Anything else worth noting', val:freeNotes, set:setFreeNotes, rows:2 },
                    ].map(({ icon, label, sub, val, set, rows }) => (
                      <div key={label}>
                        <div style={{ display:'flex', alignItems:'baseline', gap:'6px', marginBottom:'5px' }}>
                          <span style={{ fontSize:'14px' }}>{icon}</span>
                          <span style={{ fontSize:'13px', fontWeight:600, color:'#0f172a' }}>{label}</span>
                          <span style={{ fontSize:'11px', color:'#94a3b8' }}>{sub}</span>
                        </div>
                        <textarea value={val} onChange={e => set(e.target.value)} rows={rows} placeholder={`${label}...`} style={inputStyle}/>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                    <button onClick={handleSaveNotes} disabled={savingNotes} style={{ padding:'9px 20px', background:'#1e293b', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:600 }}>
                      {savingNotes ? 'Saving…' : 'Save Notes'}
                    </button>
                    {notesSaved && <span style={{ fontSize:'12px', color:'#10b981', fontWeight:600 }}>✓ Saved</span>}
                  </div>
                </div>
              )}
              {!isManager && existingComments.length > 0 && (
                <div>
                  <div style={{ fontSize:'12px', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'14px' }}>Week Notes</div>
                  {existingComments.map(c => {
                    let parsed;
                    try { parsed = JSON.parse(c.comment); } catch { parsed = { notes: c.comment }; }
                    return (
                      <div key={c.id} style={{ marginBottom:'16px' }}>
                        <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'10px' }}>
                          <strong style={{ color:'#374151' }}>{c.author_name}</strong> · {new Date(c.created_at).toLocaleDateString('en-GB')}
                        </div>
                        {[['⭐','Highlights','highlights'],['⚠️','Watch Points','watchpoints'],['🌤','Context','context'],['💬','Free Notes','notes']].map(([icon,label,key]) =>
                          parsed[key] ? (
                            <div key={key} style={{ marginBottom:'10px' }}>
                              <div style={{ fontSize:'12px', fontWeight:600, color:'#374151', marginBottom:'3px' }}>{icon} {label}</div>
                              <div style={{ fontSize:'13px', color:'#0f172a', lineHeight:1.6, paddingLeft:'6px' }}>{parsed[key]}</div>
                            </div>
                          ) : null
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    {dayModal && !isGroup && (() => {
      const dayData = daysForModal.find(d => d.dateStr === dayModal.dateStr) || dayModal;
      return (
        <DayModal
          day={dayData.day}
          date={dayData.date}
          dateStr={dayData.dateStr}
          revenue={dayData.revenue}
          lyRevenue={dayData.lyRevenue}
          fourWeekAvgRevenue={dayData.avgRev}
          priorRevs={dayData.priorRevs || []}
          venue={venue}
          days={daysForModal}
          onClose={() => setDayModal(null)}
        />
      );
    })()}
    </>
  );
}

// ── Summary Card — desktop: full card per metric; mobile: all 3 in one compact row ──
function SummaryCard({ label, actual, budget, ly, fmtFn, proRateLabel, isMobile }) {
  const budgetShortfall = fmtShortfall(actual, budget);
  const lyShortfall     = fmtShortfall(actual, ly);
  const aheadOfBudget   = budgetShortfall !== null && budgetShortfall >= 0;
  const aheadOfLY       = lyShortfall     !== null && lyShortfall     >= 0;
  const shortfallColor  = (ahead) => ahead ? '#10b981' : '#ef4444';
  const shortfallBg     = (ahead) => ahead ? '#f0fdf4' : '#fef2f2';
  const shortfallBorder = (ahead) => ahead ? '#bbf7d0' : '#fecaca';

  // Mobile: compact inline version — used inside the combined card
  if (isMobile) {
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{label}</div>
        <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '4px' }}>{actual ? fmtFn(actual) : '—'}</div>
        {lyShortfall !== null && (
          <div style={{ fontSize: '11px', fontWeight: 600, color: shortfallColor(aheadOfLY) }}>
            {aheadOfLY ? '▲' : '▼'} {fmtFn(Math.abs(Math.round(lyShortfall)))} vs LY
          </div>
        )}
      </div>
    );
  }

  // Desktop: full card
  return (
    <div style={{ flex: '1 1 260px', background: '#fff', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '32px', fontWeight: 800, color: '#0f172a', marginBottom: '6px', letterSpacing: '-0.02em' }}>{actual ? fmtFn(actual) : '—'}</div>
      {proRateLabel && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px', fontStyle: 'italic' }}>{proRateLabel}</div>}
      <div style={{ background: budgetShortfall !== null ? shortfallBg(aheadOfBudget) : '#f8fafc', border: `1px solid ${budgetShortfall !== null ? shortfallBorder(aheadOfBudget) : '#e2e8f0'}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: '2px' }}>vs Budget</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Budget: {budget ? fmtFn(Math.round(budget)) : '—'}</div>
          </div>
          {budgetShortfall !== null
            ? <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '18px', fontWeight: 800, color: shortfallColor(aheadOfBudget), lineHeight: 1 }}>{aheadOfBudget ? '+' : ''}{fmtFn(Math.round(budgetShortfall))}</div>
                <div style={{ fontSize: '11px', color: shortfallColor(aheadOfBudget), fontWeight: 600, marginTop: '2px' }}>{aheadOfBudget ? '▲ on track' : '▼ behind'}</div>
              </div>
            : <div style={{ fontSize: '12px', color: '#cbd5e1' }}>No data</div>}
        </div>
      </div>
      <div style={{ background: lyShortfall !== null ? shortfallBg(aheadOfLY) : '#f8fafc', border: `1px solid ${lyShortfall !== null ? shortfallBorder(aheadOfLY) : '#e2e8f0'}`, borderRadius: '8px', padding: '10px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: '2px' }}>vs Last Year</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>LY: {ly ? fmtFn(Math.round(ly)) : '—'}</div>
          </div>
          {lyShortfall !== null
            ? <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '18px', fontWeight: 800, color: shortfallColor(aheadOfLY), lineHeight: 1 }}>{aheadOfLY ? '+' : ''}{fmtFn(Math.round(lyShortfall))}</div>
                <div style={{ fontSize: '11px', color: shortfallColor(aheadOfLY), fontWeight: 600, marginTop: '2px' }}>{aheadOfLY ? '▲ ahead' : '▼ behind'}</div>
              </div>
            : <div style={{ fontSize: '12px', color: '#cbd5e1' }}>No data</div>}
        </div>
      </div>
    </div>
  );
}

// ── Costs sub-tab ─────────────────────────────────────────────────────────────
function CostsTab({ monthly, monthlyLY, year, venue, isMobile, externalSubTab, setExternalSubTab }) {
  const isSassys = venue === 'Sassys';
  const [internalSubTab, setInternalSubTab] = useState('payroll');

  // On mobile the sub-tab is controlled by the parent strip; on desktop it's internal
  const subTab    = isMobile ? externalSubTab    : internalSubTab;
  const setSubTab = isMobile ? setExternalSubTab : setInternalSubTab;

  const currentMonthNum = new Date().getMonth() + 1;
  const isMonthComplete = (monthNum) => year < CURRENT_YEAR || monthNum < currentMonthNum;

  const monthlyChartData = MONTHS.map((m, i) => {
    const cy = monthly.find(r => r.month === i + 1);
    const ly = monthlyLY.find(r => r.month === i + 1);
    const complete = isMonthComplete(i + 1);
    const cyRev = complete ? (cy?.actual_revenue || null) : null;
    const cyKitchenPay  = complete ? (cy?.kitchen_payroll || null) : null;
    const cyFohPay      = complete ? (isSassys ? null : (cy?.foh_payroll || null)) : null;
    const cyLmPrepPay   = complete ? (cy?.lm_prep_payroll_alloc || null) : null;
    const cyTotalPay    = complete ? ((cyKitchenPay||0) + (cyFohPay||0) + (cyLmPrepPay||0) || null) : null;
    const lyRev = ly?.actual_revenue || null;
    const lyTotalPay    = ly ? (ly.kitchen_payroll||0) + (isSassys ? 0 : (ly.foh_payroll||0)) + (ly.lm_prep_payroll_alloc||0) || null : null;
    const budgetTotalPay = cy ? (cy.budget_kitchen_payroll||0) + (cy.budget_foh_payroll||0) || null : null;
    const kitLabel = isSassys ? 'Sassys Payroll' : 'Kitchen Payroll';
    const fohLabel = isSassys ? 'LM Prep Alloc' : 'FOH Payroll';
    return {
      month: m,
      'Payroll': cyTotalPay, 'Payroll LY': lyTotalPay, 'Budget Payroll': budgetTotalPay,
      [kitLabel]: complete ? cyKitchenPay : null,
      [fohLabel]: complete ? (isSassys ? cyLmPrepPay : cyFohPay) : null,
      'Payroll % Rev':    cyRev && cyTotalPay ? parseFloat(((cyTotalPay/cyRev)*100).toFixed(1)) : null,
      'Payroll % LY':     lyRev && lyTotalPay ? parseFloat(((lyTotalPay/lyRev)*100).toFixed(1)) : null,
      'Budget Payroll %': cy?.budget_revenue && budgetTotalPay ? parseFloat(((budgetTotalPay/cy.budget_revenue)*100).toFixed(1)) : null,
      'Kitchen Hours': complete ? (cy?.kitchen_hours || null) : null,
      'FOH Hours':     complete ? (cy?.foh_hours     || null) : null,
      'Kitchen Hours LY': ly?.kitchen_hours || null,
      'FOH Hours LY':     ly?.foh_hours     || null,
      'Food Cost':    complete ? (cy?.food_cost || null) : null,
      'Food Cost LY': ly?.food_cost || null,
      'Budget Food':  cy?.budget_food_cost || null,
      'Food % Rev':    complete && cy?.food_revenue && cy?.food_cost ? parseFloat(((cy.food_cost/cy.food_revenue)*100).toFixed(1)) : null,
      'Food % LY':     ly?.food_revenue && ly?.food_cost ? parseFloat(((ly.food_cost/ly.food_revenue)*100).toFixed(1)) : null,
      'Budget Food %': cy?.food_revenue && cy?.budget_food_cost ? parseFloat(((cy.budget_food_cost/cy.food_revenue)*100).toFixed(1)) : null,
      'Bev Cost':    complete ? (cy?.bev_cost || null) : null,
      'Bev Cost LY': ly?.bev_cost || null,
      'Budget Bev':  cy?.budget_bev_cost || null,
      'Bev % Rev':    complete && cy?.bev_revenue && cy?.bev_cost ? parseFloat(((cy.bev_cost/cy.bev_revenue)*100).toFixed(1)) : null,
      'Bev % LY':     ly?.bev_revenue && ly?.bev_cost ? parseFloat(((ly.bev_cost/ly.bev_revenue)*100).toFixed(1)) : null,
      'Budget Bev %': cy?.bev_revenue && cy?.budget_bev_cost ? parseFloat(((cy.budget_bev_cost/cy.bev_revenue)*100).toFixed(1)) : null,
    };
  });

  const subTabs = [
    { key:'payroll', label:'Payroll' },
    { key:'food',    label:'Food Cost' },
    ...(!isSassys ? [{ key:'bev', label:'Bev Cost' }] : []),
  ];

  const Legend3 = ({ cy, ly, budget, colors }) => (
    <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px', flexWrap:'wrap' }}>
      {[cy, ly, budget].map((label, i) => label == null ? null : (
        <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}>
          <span style={{ width:12, height:3, background:colors[i], display:'inline-block', borderRadius:2 }}/>{label}
        </span>
      ))}
    </div>
  );

  const CostTable = ({ actualKey, budgetKey, lyKey, pctKey, pctLYKey, pctBudgetKey, label }) => (
    <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px' }}>
        <span style={{ fontWeight:700, fontSize:'14px', color:'#0f172a' }}>{label} — Monthly</span>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'11px', color:'#64748b' }}>
            <span style={{ width:10, height:10, borderRadius:'2px', background:'#EAF4F8', border:'1px solid #BCD9E5', display:'inline-block' }}/>
            Actual
          </span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'11px', color:'#94a3b8', fontStyle:'italic' }}>
            <span style={{ width:10, height:10, borderRadius:'2px', background:'#f8fafc', border:'1px solid #e2e8f0', display:'inline-block' }}/>
            Forecast
          </span>
        </div>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth: isMobile ? '320px' : undefined }}>
          <thead>
            <tr style={{ background:'#f8fafc', borderBottom:'1px solid #f1f5f9' }}>
              {['Month','Amount','% Rev','LY %','vs LY'].map(h => (
                <th key={h} style={{ padding:'9px 12px', textAlign:h==='Month'?'left':'right', fontSize:'10px', fontWeight:700, color:'#64748b', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthlyChartData.map((row, i) => {
              const actual    = row[actualKey];
              const budget    = row[budgetKey];
              const ly        = row[lyKey];
              const pctRev    = row[pctKey];
              const pctBudg   = row[pctBudgetKey];
              const pctLY     = row[pctLYKey];
              const isActual  = actual !== null && actual !== undefined;
              const amount    = isActual ? actual : budget;
              const amountPct = isActual ? pctRev : pctBudg;
              const vsLY      = isActual && ly ? actual - ly : null;
              const lyGood    = vsLY !== null && vsLY <= 0;
              return (
                <tr key={i} style={{
                  borderBottom:'1px solid #f8fafc',
                  background: isActual ? (i%2===0?'#EAF4F8':'#dff0f7') : (i%2===0?'#fff':'#fafafa'),
                }}>
                  <td style={{ padding:'9px 12px', fontSize:'13px', fontWeight: isActual ? 600 : 400, color: isActual ? '#0f172a' : '#94a3b8' }}>
                    {MONTHS[i]}
                  </td>
                  <td style={{ padding:'9px 12px', textAlign:'right', fontWeight: isActual ? 700 : 400, fontSize:'13px', color: isActual ? '#0f172a' : '#94a3b8', fontStyle: isActual ? 'normal' : 'italic' }}>
                    {amount ? fmt(amount) : '—'}
                  </td>
                  <td style={{ padding:'9px 12px', textAlign:'right', fontSize:'12px', color: isActual ? '#374151' : '#94a3b8', fontStyle: isActual ? 'normal' : 'italic' }}>
                    {amountPct ? amountPct+'%' : '—'}
                  </td>
                  <td style={{ padding:'9px 12px', textAlign:'right', fontSize:'12px', color:'#64748b' }}>
                    {pctLY ? pctLY+'%' : '—'}
                  </td>
                  <td style={{ padding:'9px 12px', textAlign:'right', fontSize:'12px', fontWeight:600, color: vsLY!==null ? (lyGood?'#10b981':'#ef4444') : '#cbd5e1' }}>
                    {vsLY!==null ? `${lyGood?'▼':'+'}${fmt(Math.abs(Math.round(vsLY)))}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );



  return (
    <div>
      {!isMobile && (
        <div style={{ display:'flex', gap:'6px', marginBottom:'20px', flexWrap:'wrap' }}>
          {subTabs.map(t => <button key={t.key} onClick={()=>setSubTab(t.key)} style={pill(subTab===t.key,true)}>{t.label}</button>)}
        </div>
      )}

      {subTab==='payroll' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'16px' }}>
            <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Total Payroll €</div>
              <Legend3 cy={year} ly={year-1} budget={null} colors={['#6366f1','#a5b4fc','#94a3b8']}/>
              <ResponsiveContainer width='100%' height={180}>
                <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/><XAxis dataKey='month' tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} width={48}/><Tooltip formatter={fmt}/>
                  <Line type='monotone' dataKey='Payroll' stroke='#6366f1' strokeWidth={2} dot={{r:3}}/>
                  <Line type='monotone' dataKey='Payroll LY' stroke='#a5b4fc' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Payroll % of Revenue</div>
              <Legend3 cy={year} ly={year-1} budget={null} colors={['#6366f1','#a5b4fc','#94a3b8']}/>
              <ResponsiveContainer width='100%' height={180}>
                <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/><XAxis dataKey='month' tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`${v}%`} domain={['auto','auto']} width={40}/><Tooltip formatter={v=>`${v}%`}/>
                  <Line type='monotone' dataKey='Payroll % Rev' stroke='#6366f1' strokeWidth={2} dot={{r:3}}/>
                  <Line type='monotone' dataKey='Payroll % LY' stroke='#a5b4fc' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'12px' }}>{isSassys ? 'Sassys Payroll vs LM Prep Allocation €' : 'Kitchen vs FOH Payroll €'}</div>
              <ResponsiveContainer width='100%' height={180}>
                <BarChart data={monthlyChartData}><CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/><XAxis dataKey='month' tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} width={48}/><Tooltip formatter={fmt}/><Legend wrapperStyle={{fontSize:'11px'}}/>
                  <Bar dataKey={isSassys ? 'Sassys Payroll' : 'Kitchen Payroll'} stackId='a' fill='#f59e0b'/>
                  <Bar dataKey={isSassys ? 'LM Prep Alloc' : 'FOH Payroll'} stackId='a' fill='#6366f1' radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>{isSassys ? 'Kitchen Hours' : 'Kitchen vs FOH Hours'}</div>
              <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px', flexWrap:'wrap' }}>
                {[['Kitchen',`${year}`,'#f59e0b'],['FOH',`${year}`,'#6366f1'],['Kitchen LY',`${year-1}`,'#fcd34d'],['FOH LY',`${year-1}`,'#a5b4fc']].map(([l,yr,c])=>(
                  <span key={l+yr} style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}>
                    <span style={{ width:12, height:3, background:c, display:'inline-block', borderRadius:2, opacity: yr===String(year) ? 1 : 0.6 }}/>{l}
                  </span>
                ))}
              </div>
              <ResponsiveContainer width='100%' height={180}>
                <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/><XAxis dataKey='month' tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} width={40}/><Tooltip/>
                  <Line type='monotone' dataKey='Kitchen Hours' stroke='#f59e0b' strokeWidth={2} dot={{r:3}}/>
                  {!isSassys && <Line type='monotone' dataKey='FOH Hours' stroke='#6366f1' strokeWidth={2} dot={{r:3}}/>}
                  <Line type='monotone' dataKey='Kitchen Hours LY' stroke='#fcd34d' strokeWidth={1.5} strokeDasharray='4 2' dot={false}/>
                  {!isSassys && <Line type='monotone' dataKey='FOH Hours LY' stroke='#a5b4fc' strokeWidth={1.5} strokeDasharray='4 2' dot={false}/>}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <CostTable label='Payroll' actualKey='Payroll' budgetKey='Budget Payroll' lyKey='Payroll LY' pctKey='Payroll % Rev' pctLYKey='Payroll % LY' pctBudgetKey='Budget Payroll %'/>
        </div>
      )}

      {subTab==='food' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'16px' }}>
            <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Food Cost €</div>
              <Legend3 cy={year} ly={year-1} budget={null} colors={['#f59e0b','#fcd34d','#94a3b8']}/>
              <ResponsiveContainer width='100%' height={180}>
                <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/><XAxis dataKey='month' tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} width={48}/><Tooltip formatter={fmt}/>
                  <Line type='monotone' dataKey='Food Cost' stroke='#f59e0b' strokeWidth={2} dot={{r:3}}/>
                  <Line type='monotone' dataKey='Food Cost LY' stroke='#fcd34d' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Food Cost % of Food Revenue</div>
              <Legend3 cy={year} ly={year-1} budget={null} colors={['#f59e0b','#fcd34d','#94a3b8']}/>
              <ResponsiveContainer width='100%' height={180}>
                <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/><XAxis dataKey='month' tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`${v}%`} domain={[0, 45]} width={40}/><Tooltip formatter={v=>`${v}%`}/>
                  <Line type='monotone' dataKey='Food % Rev' stroke='#f59e0b' strokeWidth={2} dot={{r:3}}/>
                  <Line type='monotone' dataKey='Food % LY' stroke='#fcd34d' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <CostTable label='Food Cost' actualKey='Food Cost' budgetKey='Budget Food' lyKey='Food Cost LY' pctKey='Food % Rev' pctLYKey='Food % LY' pctBudgetKey='Budget Food %'/>
        </div>
      )}

      {subTab==='bev' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'16px' }}>
            <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Bev Cost €</div>
              <Legend3 cy={year} ly={year-1} budget={null} colors={['#3A8FA3','#BCD9E5','#94a3b8']}/>
              <ResponsiveContainer width='100%' height={180}>
                <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/><XAxis dataKey='month' tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} width={48}/><Tooltip formatter={fmt}/>
                  <Line type='monotone' dataKey='Bev Cost' stroke='#3A8FA3' strokeWidth={2} dot={{r:3}}/>
                  <Line type='monotone' dataKey='Bev Cost LY' stroke='#BCD9E5' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Bev Cost % of Bev Revenue</div>
              <Legend3 cy={year} ly={year-1} budget={null} colors={['#3A8FA3','#BCD9E5','#94a3b8']}/>
              <ResponsiveContainer width='100%' height={180}>
                <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/><XAxis dataKey='month' tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`${v}%`} domain={[0, 45]} width={40}/><Tooltip formatter={v=>`${v}%`}/>
                  <Line type='monotone' dataKey='Bev % Rev' stroke='#3A8FA3' strokeWidth={2} dot={{r:3}}/>
                  <Line type='monotone' dataKey='Bev % LY' stroke='#BCD9E5' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <CostTable label='Bev Cost' actualKey='Bev Cost' budgetKey='Budget Bev' lyKey='Bev Cost LY' pctKey='Bev % Rev' pctLYKey='Bev % LY' pctBudgetKey='Budget Bev %'/>
        </div>
      )}

    </div>
  );
}

// ── Mobile Weekly Table Row ───────────────────────────────────────────────────
function MobileWeekRow({ r, lyRow, monthLabel, showMonthLabel, onClick, weekRank }) {
  const lyRevDiff = lyRow ? r.total_revenue - lyRow.total_revenue : null;
  const lyRevPct  = lyRow ? pct(r.total_revenue, lyRow.total_revenue) : null;
  const lyAhead   = lyRevDiff !== null && lyRevDiff >= 0;
  const isTop3    = weekRank !== null && weekRank >= 1 && weekRank <= 3;
  const rankEmoji = isTop3 ? `🍋${weekRank}` : null;
  return (
    <>
      {showMonthLabel && (
        <div style={{ padding:'6px 14px', background:'#f1f5f9', fontSize:'10px', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>{monthLabel}</div>
      )}
      <div onClick={onClick} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', borderBottom:'1px solid #f1f5f9', cursor:'pointer', background: isTop3 ? '#f0fdf4' : '#fff', gap:'8px' }}>
        <div style={{ minWidth:'36px', textAlign:'center' }}>
          <div style={{ fontWeight:700, fontSize:'14px', color: isTop3 ? '#15803d' : '#0f172a' }}>W{r.week}</div>
          {isTop3 && <div style={{ fontSize:'9px', fontWeight:800, color:'#15803d', marginTop:'1px' }}>{rankEmoji}</div>}
        </div>
        <div style={{ flex:1 }}><div style={{ fontWeight:800, fontSize:'15px', color:'#0f172a' }}>{fmt(r.total_revenue)}</div></div>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          {lyRevDiff !== null ? (
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:'12px', fontWeight:700, color: lyAhead ? '#10b981' : '#ef4444' }}>{lyAhead ? '+' : ''}{fmt(Math.round(lyRevDiff))}</div>
              <div style={{ fontSize:'10px', color: lyAhead ? '#10b981' : '#ef4444', fontWeight:500 }}>{lyAhead ? '▲' : '▼'} {Math.abs(lyRevPct)}%</div>
            </div>
          ) : <span style={{ fontSize:'11px', color:'#cbd5e1' }}>No LY</span>}
          <span style={{ color:'#94a3b8', fontSize:'14px' }}>›</span>
        </div>
      </div>
    </>
  );
}

// ── Venue dropdown — calendar style ──────────────────────────────────────────
const VENUE_DOT_COLORS = {
  'Box Jordaan':  '#E4AC49',
  'Box Plantage': '#3A8FA3',
  'Sassys':       '#7C3AED',
  'Group':        '#7EA594',
};

function VenueDropdown({ venue, allowedVenues, isAdmin, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const options = [...allowedVenues, ...(isAdmin ? ['Group'] : [])];
  const dotColor = VENUE_DOT_COLORS[venue] || '#94a3b8';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position:'relative', flexShrink:0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display:'flex', alignItems:'center', gap:'6px',
          padding:'5px 10px', borderRadius:'20px',
          border:`1.5px solid ${open ? dotColor : '#e2e8f0'}`,
          background:'#fff', cursor:'pointer',
          fontSize:'12px', fontWeight:600, color:'#0f172a',
          transition:'all 0.15s',
          boxShadow: open ? `0 0 0 3px ${dotColor}18` : 'none',
        }}
      >
        <span style={{ width:7, height:7, borderRadius:'50%', background:dotColor, flexShrink:0 }} />
        {venue.replace('Box ', '')}
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ transition:'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', color:'#94a3b8' }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', right:0,
          background:'#fff', borderRadius:'12px',
          border:'1px solid #e2e8f0',
          boxShadow:'0 8px 24px rgba(15,23,42,0.12)',
          zIndex:300, minWidth:'160px', padding:'4px',
        }}>
          {options.map(v => {
            const isActive = venue === v;
            const vDot = VENUE_DOT_COLORS[v] || '#94a3b8';
            return (
              <button
                key={v}
                onClick={() => { onChange(v); setOpen(false); }}
                style={{
                  width:'100%', display:'flex', alignItems:'center', gap:'8px',
                  padding:'7px 10px', borderRadius:'8px', border:'none',
                  background: isActive ? `${vDot}12` : 'transparent',
                  cursor:'pointer', textAlign:'left',
                  fontSize:'13px', fontWeight: isActive ? 700 : 500,
                  color: isActive ? vDot : '#0f172a',
                  transition:'background 0.1s',
                }}
              >
                <span style={{ width:7, height:7, borderRadius:'50%', background:vDot, flexShrink:0 }} />
                {v.replace('Box ', '')}
                {isActive && (
                  <span style={{ marginLeft:'auto', width:5, height:5, borderRadius:'50%', background:vDot }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Weekly Charts Panel (desktop right column + reused on mobile) ─────────────
function WeeklyChartsPanel({ chartData, chartData8, year, isGroup }) {
  const [showHoursOnCovers, setShowHoursOnCovers] = useState(false);
  const revDomain    = yDomain(chartData, ['Revenue', 'Revenue LY']);
  const coversDomain = yDomain(chartData, ['Covers', 'Covers LY']);
  const rpcDomain    = yDomain(chartData8, ['Rev/Cover', 'Rev/Cover LY'], 0.15);
  const hoursDomain  = yDomain(chartData, ['Hours', 'Hours LY']);

  const hasHours = chartData.some(d => d['Hours'] != null || d['Hours LY'] != null);
  const hasCoversHours = showHoursOnCovers && hasHours;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

      {/* Revenue Trend */}
      <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Revenue Trend</div>
        <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#3A8FA3', display:'inline-block', borderRadius:2 }}/>{year}</span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#7A8A96', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="week" tick={{fontSize:10}}/>
            <YAxis tick={{fontSize:10}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} domain={revDomain} width={45}/>
            <Tooltip formatter={fmt}/>
            <Line type="monotone" dataKey="Revenue" stroke="#3A8FA3" strokeWidth={2.5} dot={false} connectNulls/>
            <Line type="monotone" dataKey="Revenue LY" stroke="#7A8A96" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Hours Trend */}
      {hasHours && (
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Hours Trend</div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#E4AC49', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#C4860A', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="week" tick={{fontSize:10}}/>
              <YAxis tick={{fontSize:10}} domain={hoursDomain} width={35}/>
              <Tooltip formatter={v => v != null ? v + 'h' : '—'}/>
              <Line type="monotone" dataKey="Hours" stroke="#E4AC49" strokeWidth={2.5} dot={false} connectNulls/>
              <Line type="monotone" dataKey="Hours LY" stroke="#C4860A" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Revenue per Hour — full year */}
      {chartData.some(d => d['Rev/Hr'] != null) && (
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Revenue per Hour Worked</div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#7C3AED', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#c4b5fd', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="week" tick={{fontSize:10}}/>
              <YAxis tick={{fontSize:10}} tickFormatter={v=>`€${v}`} domain={yDomain(chartData,['Rev/Hr','Rev/Hr LY'],0.15)} width={45}/>
              <Tooltip formatter={fmt}/>
              <Line type="monotone" dataKey="Rev/Hr" stroke="#7C3AED" strokeWidth={2} dot={false} connectNulls/>
              <Line type="monotone" dataKey="Rev/Hr LY" stroke="#c4b5fd" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Brunch vs Dinner */}
      {!isGroup && chartData.some(d => d['Brunch'] != null || d['Dinner'] != null) && (
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Brunch vs Dinner</div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#E4AC49', display:'inline-block', borderRadius:2 }}/>Brunch</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#445366', display:'inline-block', borderRadius:2 }}/>Dinner</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="week" tick={{fontSize:10}}/>
              <YAxis tick={{fontSize:10}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} domain={['auto','auto']} width={45}/>
              <Tooltip formatter={fmt}/>
              <Line type="monotone" dataKey="Brunch" stroke="#E4AC49" strokeWidth={2} dot={false} connectNulls/>
              <Line type="monotone" dataKey="Dinner" stroke="#445366" strokeWidth={2} dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Covers Trend — with optional hours overlay */}
      <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', flex:1 }}>Covers Trend</div>
          {hasHours && (
            <button onClick={() => setShowHoursOnCovers(v => !v)} style={{
              fontSize:'10px', fontWeight:600, padding:'3px 9px', borderRadius:'20px', border:'none', cursor:'pointer',
              background: showHoursOnCovers ? '#FDF3DC' : '#f1f5f9',
              color: showHoursOnCovers ? '#E4AC49' : '#94a3b8',
              transition:'all 0.15s',
            }}>
              {showHoursOnCovers ? '◉ Hours on' : '○ + Hours'}
            </button>
          )}
        </div>
        <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px', flexWrap:'wrap' }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#6366f1', display:'inline-block', borderRadius:2 }}/>{year} Covers</span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#a5b4fc', display:'inline-block', borderRadius:2 }}/>{year-1} Covers</span>
          {hasCoversHours && <>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#E4AC49', display:'inline-block', borderRadius:2, opacity:0.9 }}/>{year} Hours</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#C4860A', display:'inline-block', borderRadius:2 }}/>{year-1} Hours</span>
          </>}
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="week" tick={{fontSize:10}}/>
            <YAxis yAxisId="covers" tick={{fontSize:10}} domain={coversDomain} width={40}/>
            {hasCoversHours && (
              <YAxis yAxisId="hours" orientation="right" tick={{fontSize:10}} width={35} tickFormatter={v=>v+'h'}/>
            )}
            <Tooltip formatter={(val, name) => name.includes('Hours') ? (val != null ? val+'h' : '—') : fmtNum(val)}/>
            <Line yAxisId="covers" type="monotone" dataKey="Covers" stroke="#6366f1" strokeWidth={2} dot={false} connectNulls/>
            <Line yAxisId="covers" type="monotone" dataKey="Covers LY" stroke="#a5b4fc" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            {hasCoversHours && <>
              <Line yAxisId="hours" type="monotone" dataKey="Hours" stroke="#E4AC49" strokeWidth={2} dot={false} connectNulls/>
              <Line yAxisId="hours" type="monotone" dataKey="Hours LY" stroke="#C4860A" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            </>}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue per Cover — last 8 weeks */}
      {(()=>{ return (
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Revenue per Cover <span style={{ fontSize:'11px', color:'#94a3b8', fontWeight:400 }}>— last 8 weeks</span></div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#10b981', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#6ee7b7', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData8}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="week" tick={{fontSize:10}}/>
              <YAxis tick={{fontSize:10}} tickFormatter={v=>`€${v}`} domain={rpcDomain} width={45}/>
              <Tooltip formatter={fmt}/>
              <Line type="monotone" dataKey="Rev/Cover" stroke="#10b981" strokeWidth={2} dot={false} connectNulls/>
              <Line type="monotone" dataKey="Rev/Cover LY" stroke="#6ee7b7" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      ); })()}
    </div>
  );
}

// ── Month Modal ───────────────────────────────────────────────────────────────
function MonthModal({ monthNum, monthly, monthlyLY, dailyData, dailyDataLY, venue, year, onClose }) {
  const isMobile = useIsMobile();
  const [currentMonth, setCurrentMonth] = useState(monthNum);
  const isSassys = venue === 'Sassys';
  const isGroup  = venue === 'Group';

  // Available months with any data
  const availableMonths = MONTHS.map((_, i) => i + 1).filter(m => {
    const hasMonthly = monthly.find(r => r.month === m);
    const hasDailyRev = dailyData.some(d =>
      (isGroup || d.venue === venue) && new Date(d.date).getMonth() + 1 === m && (d.total_revenue || 0) > 0
    );
    return hasMonthly || hasDailyRev;
  });

  const prevMonth = availableMonths[availableMonths.indexOf(currentMonth) - 1] ?? null;
  const nextMonth = availableMonths[availableMonths.indexOf(currentMonth) + 1] ?? null;

  // Revenue helpers
  const dailyRev = (dataArr, m) =>
    dataArr
      .filter(d => (isGroup || d.venue === venue) && new Date(d.date).getMonth() + 1 === m && (d.total_revenue || 0) > 0)
      .reduce((s, d) => s + (d.total_revenue || 0), 0) || null;

  const getRevenue = (m, dataArr, monthlyArr) => {
    const dr = dailyRev(dataArr, m);
    return dr || monthlyArr.find(r => r.month === m)?.actual_revenue || null;
  };

  const row   = monthly.find(r => r.month === currentMonth);
  const rowLY = monthlyLY.find(r => r.month === currentMonth);

  const cy = {
    revenue:         getRevenue(currentMonth, dailyData, monthly),
    food_cost:       row?.food_cost || null,
    bev_cost:        row?.bev_cost  || null,
    oedipus:         row ? (row.oedipus_rent || 0) + (row.oedipus_utilities || 0) + (row.oedipus_rev_share || 0) : null,
    kitchen_payroll: row?.kitchen_payroll || null,
    foh_payroll:     row?.foh_payroll     || null,
    lm_total:        row ? (row.lm_prep_payroll_alloc || 0) + (row.lm_transfers_alloc || 0) : null,
    group_costs:     row?.group_costs || null,
    other_opex:      row?.other_opex  || null,
    covers:          row?.actual_covers || null,
    kitchen_hours:   row?.kitchen_hours || null,
    foh_hours:       row?.foh_hours     || null,
  };
  cy.payroll = (cy.kitchen_payroll || 0) + (isSassys ? 0 : (cy.foh_payroll || 0));
  cy.ebitda  = cy.revenue
    ? cy.revenue
      - (cy.food_cost || 0)
      - (isSassys ? 0 : (cy.bev_cost || 0))
      - (isSassys ? (cy.oedipus || 0) : 0)
      - cy.payroll
      - (cy.lm_total || 0)
      - (cy.group_costs || 0)
      - (cy.other_opex || 0)
    : null;
  cy.totalExpense = cy.ebitda !== null ? cy.revenue - cy.ebitda : null;

  const ly = {
    revenue:         getRevenue(currentMonth, dailyDataLY, monthlyLY),
    food_cost:       rowLY?.food_cost || null,
    bev_cost:        rowLY?.bev_cost  || null,
    oedipus:         rowLY ? (rowLY.oedipus_rent || 0) + (rowLY.oedipus_utilities || 0) + (rowLY.oedipus_rev_share || 0) : null,
    kitchen_payroll: rowLY?.kitchen_payroll || null,
    foh_payroll:     rowLY?.foh_payroll     || null,
    lm_total:        rowLY ? (rowLY.lm_prep_payroll_alloc || 0) + (rowLY.lm_transfers_alloc || 0) : null,
    group_costs:     rowLY?.group_costs || null,
    other_opex:      rowLY?.other_opex  || null,
    covers:          rowLY?.actual_covers || null,
  };
  ly.payroll = (ly.kitchen_payroll || 0) + (isSassys ? 0 : (ly.foh_payroll || 0));
  ly.ebitda  = ly.revenue
    ? ly.revenue
      - (ly.food_cost || 0)
      - (isSassys ? 0 : (ly.bev_cost || 0))
      - (isSassys ? (ly.oedipus || 0) : 0)
      - ly.payroll
      - (ly.lm_total || 0)
      - (ly.group_costs || 0)
      - (ly.other_opex || 0)
    : null;
  ly.totalExpense = ly.ebitda !== null ? ly.revenue - ly.ebitda : null;

  const PLRowM = ({ label, actual, lyVal, indent=false, bold=false, highlight=false, isRevenue=false, dimIfZero=false }) => {
    const vsLY  = actual && lyVal ? actual - lyVal : null;
    const lyGood = vsLY !== null && (isRevenue ? vsLY >= 0 : vsLY <= 0);
    if (dimIfZero && (!actual || actual === 0)) return null;
    return (
      <tr style={{ borderBottom:'1px solid #f1f5f9', background: highlight ? '#FEFCE8' : 'transparent' }}>
        <td style={{ padding: isMobile ? '8px 12px' : '9px 16px', paddingLeft: indent ? (isMobile?'22px':'28px') : (isMobile?'12px':'16px'), fontSize:'13px', fontWeight:bold?700:400, color:'#2F3A42' }}>{label}</td>
        <td style={{ padding: isMobile ? '8px 10px' : '9px 16px', textAlign:'right', fontWeight:bold?800:600, fontSize:'13px', color:'#2F3A42' }}>{actual ? fmt(actual) : '—'}</td>
        <td style={{ padding: isMobile ? '8px 10px' : '9px 16px', textAlign:'right', fontSize:'12px', color:'#7A8A96' }}>{lyVal ? fmt(lyVal) : '—'}</td>
        <td style={{ padding: isMobile ? '8px 10px' : '9px 16px', textAlign:'right', fontSize:'12px' }}>
          {vsLY !== null
            ? <span style={{ fontWeight:700, color: lyGood ? '#7EA594' : '#DC3545' }}>{vsLY >= 0 ? '+' : ''}{fmt(Math.round(vsLY))}</span>
            : '—'}
        </td>
      </tr>
    );
  };

  const fmtPctM = (cost, rev) => (cost && rev) ? ((cost / rev) * 100).toFixed(1) + '%' : '—';

  const kpis = [
    { label: 'Food Cost %', actual: fmtPctM(cy.food_cost, cy.revenue),  ly: fmtPctM(ly.food_cost, ly.revenue) },
    ...(!isSassys ? [{ label: 'Bev Cost %', actual: fmtPctM(cy.bev_cost, cy.revenue), ly: fmtPctM(ly.bev_cost, ly.revenue) }] : []),
    ...(isSassys  ? [{ label: 'Oedipus %',  actual: fmtPctM(cy.oedipus,  cy.revenue), ly: fmtPctM(ly.oedipus,  ly.revenue) }] : []),
    { label: 'Payroll %',  actual: fmtPctM(cy.payroll, cy.revenue),      ly: fmtPctM(ly.payroll, ly.revenue) },
    { label: 'EBITDA %',   actual: fmtPctM(cy.ebitda,  cy.revenue),      ly: fmtPctM(ly.ebitda,  ly.revenue) },
    { label: 'Covers',     actual: cy.covers ? fmtNum(cy.covers) : '—',  ly: ly.covers ? fmtNum(ly.covers) : '—' },
    { label: 'Avg Check',  actual: cy.covers && cy.revenue ? fmt(Math.round(cy.revenue / cy.covers)) : '—',
                           ly:     ly.covers && ly.revenue ? fmt(Math.round(ly.revenue / ly.covers)) : '—' },
    ...((() => {
      const cyH = (cy.kitchen_hours||0) + (cy.foh_hours||0);
      const lyH = (rowLY?.kitchen_hours||0) + (rowLY?.foh_hours||0);
      const cyRH = cyH > 0 && cy.revenue ? Math.round(cy.revenue / cyH) : null;
      const lyRH = lyH > 0 && ly.revenue ? Math.round(ly.revenue / lyH) : null;
      if (!cyRH) return [];
      const diff = cyRH && lyRH ? cyRH - lyRH : null;
      const good = diff !== null ? diff >= 0 : null;
      return [{ label: 'Rev/Hr', actual: fmt(cyRH), ly: lyRH ? `${fmt(lyRH)} ${good ? '▲' : '▼'}` : null }];
    })()),
  ];

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:500,
      background:'rgba(15,23,42,0.55)', backdropFilter:'blur(3px)',
      display:'flex', alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent:'center', padding: isMobile ? 0 : '16px',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background:'#fff',
        borderRadius: isMobile ? '20px 20px 0 0' : '16px',
        width: isMobile ? '100%' : '560px',
        maxHeight: isMobile ? '92vh' : '88vh',
        display:'flex', flexDirection:'column',
        boxShadow:'0 20px 60px rgba(0,0,0,0.2)',
        overflow:'hidden',
      }}>

        {/* Header */}
        <div style={{ padding: isMobile ? '16px 16px 12px' : '20px 24px 14px', borderBottom:'1px solid #f1f5f9', background:'#F7F6EF', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
            <div>
              <div style={{ fontSize:'11px', fontWeight:700, color:'#7A8A96', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'2px' }}>{venue} · {year}</div>
              <div style={{ fontSize:'22px', fontWeight:800, color:'#0f172a', letterSpacing:'-0.02em' }}>{MONTHS[currentMonth - 1]}</div>
            </div>
            <button onClick={onClose} style={{ background:'#e2e8f0', border:'none', borderRadius:'50%', width:30, height:30, cursor:'pointer', fontSize:'16px', color:'#64748b', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>✕</button>
          </div>

          {/* Month stepper */}
          <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            <button
              onClick={() => prevMonth && setCurrentMonth(prevMonth)}
              disabled={!prevMonth}
              style={{ padding:'4px 12px', borderRadius:'20px', border:'none', cursor: prevMonth ? 'pointer' : 'default', fontSize:'12px', fontWeight:600, background: prevMonth ? '#1e293b' : '#e2e8f0', color: prevMonth ? '#fff' : '#94a3b8', opacity: prevMonth ? 1 : 0.5 }}
            >← {prevMonth ? MONTHS[prevMonth - 1] : '—'}</button>
            <span style={{ flex:1 }}/>
            <button
              onClick={() => nextMonth && setCurrentMonth(nextMonth)}
              disabled={!nextMonth}
              style={{ padding:'4px 12px', borderRadius:'20px', border:'none', cursor: nextMonth ? 'pointer' : 'default', fontSize:'12px', fontWeight:600, background: nextMonth ? '#1e293b' : '#e2e8f0', color: nextMonth ? '#fff' : '#94a3b8', opacity: nextMonth ? 1 : 0.5 }}
            >{nextMonth ? MONTHS[nextMonth - 1] : '—'} →</button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div style={{ overflowY:'auto', flex:1, padding: isMobile ? '16px' : '20px 24px' }}>

          {/* Revenue hero */}
          {cy.revenue && (
            <div style={{ display:'flex', gap:'12px', marginBottom:'20px', flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:'11px', color:'#7A8A96', fontWeight:600, textTransform:'uppercase', marginBottom:'3px' }}>Revenue</div>
                <div style={{ fontSize:'28px', fontWeight:800, color:'#0f172a', letterSpacing:'-0.02em' }}>{fmt(cy.revenue)}</div>
                {ly.revenue && (
                  <div style={{ fontSize:'12px', fontWeight:600, color: cy.revenue >= ly.revenue ? '#7EA594' : '#DC3545', marginTop:'3px' }}>
                    {cy.revenue >= ly.revenue ? '▲' : '▼'} {fmt(Math.abs(Math.round(cy.revenue - ly.revenue)))} vs LY ({MONTHS[currentMonth-1]} {year-1}: {fmt(ly.revenue)})
                  </div>
                )}
              </div>
              {cy.ebitda !== null && (
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:'11px', color:'#7A8A96', fontWeight:600, textTransform:'uppercase', marginBottom:'3px' }}>EBITDA</div>
                  <div style={{ fontSize:'22px', fontWeight:800, color: cy.ebitda >= 0 ? '#7EA594' : '#DC3545' }}>{fmt(Math.round(cy.ebitda))}</div>
                  {ly.ebitda !== null && (
                    <div style={{ fontSize:'11px', color:'#7A8A96', marginTop:'2px' }}>LY: {fmt(Math.round(ly.ebitda))}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* P&L table */}
          <div style={{ overflowX:'auto', marginBottom:'20px', borderRadius:'10px', border:'1px solid #e2e8f0', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F7F6EF', borderBottom:'1px solid #D8DEE3' }}>
                  {['', 'Actual', String(year-1), 'vs LY'].map((h, i) => (
                    <th key={i} style={{ padding: isMobile ? '8px 10px' : '9px 16px', textAlign:i===0?'left':'right', fontSize:'10px', fontWeight:700, color:'#7A8A96', textTransform:'uppercase', letterSpacing:'0.04em', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <PLRowM label='Revenue'         actual={cy.revenue}         lyVal={ly.revenue}         bold isRevenue/>
                <PLRowM label='Food Cost'        actual={cy.food_cost}       lyVal={ly.food_cost}       indent/>
                {!isSassys && <PLRowM label='Bev Cost'       actual={cy.bev_cost}        lyVal={ly.bev_cost}        indent dimIfZero/>}
                {isSassys  && <PLRowM label='Oedipus Costs'  actual={cy.oedipus}         lyVal={ly.oedipus}         indent dimIfZero/>}
                <PLRowM label='Payroll'          actual={cy.payroll || null} lyVal={ly.payroll || null} indent/>
                {cy.lm_total > 0 && <PLRowM label='LM Allocations' actual={cy.lm_total} lyVal={ly.lm_total > 0 ? ly.lm_total : null} indent dimIfZero/>}
                {(cy.group_costs > 0 || cy.other_opex > 0) && <PLRowM label='Other OpEx' actual={(cy.group_costs||0)+(cy.other_opex||0)} lyVal={((ly.group_costs||0)+(ly.other_opex||0))||null} indent dimIfZero/>}
                <PLRowM label='Total Expenses' actual={cy.totalExpense} lyVal={ly.totalExpense} bold/>
                <PLRowM label='EBITDA'           actual={cy.ebitda !== null ? Math.round(cy.ebitda) : null} lyVal={ly.ebitda !== null ? Math.round(ly.ebitda) : null} bold highlight isRevenue/>
              </tbody>
            </table>
          </div>

          {/* KPI chips */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(110px, 1fr))', gap:'8px', marginBottom:'16px' }}>
            {kpis.map(item => (
              <div key={item.label} style={{ background:'#F7F6EF', borderRadius:'8px', padding:'10px 12px', border:'1px solid #D8DEE3' }}>
                <div style={{ fontSize:'10px', color:'#7A8A96', textTransform:'uppercase', fontWeight:700, marginBottom:'3px' }}>{item.label}</div>
                <div style={{ fontSize:'15px', fontWeight:800, color:'#2F3A42' }}>{item.actual}</div>
                {item.ly && item.ly !== '—' && <div style={{ fontSize:'10px', color:'#7A8A96', marginTop:'2px' }}>LY: {item.ly}</div>}
              </div>
            ))}
          </div>

          {/* Hours if available */}
          {(cy.kitchen_hours || cy.foh_hours) && (() => {
            const lyKitchen = rowLY?.kitchen_hours || null;
            const lyFoh     = rowLY?.foh_hours     || null;
            const lyTotal   = (lyKitchen||0) + (isSassys ? 0 : (lyFoh||0));
            const fromEitje = false;
            return (
            <div style={{ background:'#FDF3DC', borderRadius:'10px', padding:'12px 14px', border:'1px solid #E4AC49' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                <div style={{ fontSize:'11px', fontWeight:700, color:'#92400e', textTransform:'uppercase' }}>
                  Hours <span style={{ fontSize:'10px', fontWeight:500, color:'#b45309', textTransform:'none' }}>· {fromEitje ? 'via Eitje' : 'manual upload'}</span>
                </div>
                {lyTotal > 0 && (
                  <div style={{ fontSize:'10px', color:'#b45309' }}>LY: {Math.round(lyTotal)}h total</div>
                )}
              </div>
              <div style={{ display:'flex', gap:'16px', flexWrap:'wrap' }}>
                {cy.kitchen_hours > 0 && (
                  <div>
                    <div style={{ fontSize:'10px', color:'#92400e' }}>Kitchen</div>
                    <div style={{ fontSize:'15px', fontWeight:800, color:'#92400e' }}>{Math.round(cy.kitchen_hours)}h</div>
                    {lyKitchen > 0 && <div style={{ fontSize:'10px', color:'#b45309' }}>LY: {Math.round(lyKitchen)}h</div>}
                  </div>
                )}
                {!isSassys && cy.foh_hours > 0 && (
                  <div>
                    <div style={{ fontSize:'10px', color:'#92400e' }}>FOH</div>
                    <div style={{ fontSize:'15px', fontWeight:800, color:'#92400e' }}>{Math.round(cy.foh_hours)}h</div>
                    {lyFoh > 0 && <div style={{ fontSize:'10px', color:'#b45309' }}>LY: {Math.round(lyFoh)}h</div>}
                  </div>
                )}
                <div>
                  <div style={{ fontSize:'10px', color:'#92400e' }}>Total</div>
                  <div style={{ fontSize:'15px', fontWeight:800, color:'#92400e' }}>{Math.round((cy.kitchen_hours||0) + (isSassys ? 0 : (cy.foh_hours||0)))}h</div>
                </div>
              </div>
            </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
}

// ── Monthly Tab ───────────────────────────────────────────────────────────────
function MonthlyTab({ monthly, monthlyLY, monthlyChartData, dailyData, dailyDataLY, venue, year, isMobile }) {
  const [showHoursOnCovers, setShowHoursOnCovers] = useState(false);
  const [modalMonth, setModalMonth] = useState(null);
  const hasHours = monthlyChartData.some(d => d['Hours'] != null || d['Hours LY'] != null);
  const hasCoversHours = showHoursOnCovers && hasHours;

  const MonthlyTable = () => (
    <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', fontWeight:700, fontSize:'14px', color:'#0f172a' }}>Monthly Results — {venue} {year}</div>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead>
          <tr style={{ borderBottom:'1px solid #f1f5f9', background:'#f8fafc' }}>
            {['Month','Revenue','vs LY (€)','Covers','Rev/Hr',''].map(h=>(
              <th key={h} style={{ padding:'10px 12px', textAlign:h==='Month'?'left':'right', fontSize:'11px', fontWeight:700, color:'#64748b', textTransform:'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MONTHS.map((m,i) => {
            const row   = monthly.find(r=>r.month===i+1);
            const rowLY = monthlyLY.find(r=>r.month===i+1);
            const monthNum = i + 1;
            // Revenue from dailyData first, fallback to results_monthly for 2024
            const dailyRevCY = dailyData.filter(d=>(venue==='Group'||d.venue===venue)&&new Date(d.date).getMonth()+1===monthNum&&(d.total_revenue||0)>0).reduce((s,d)=>s+(d.total_revenue||0),0);
            const dailyRevLYM = dailyDataLY.filter(d=>(venue==='Group'||d.venue===venue)&&new Date(d.date).getMonth()+1===monthNum&&(d.total_revenue||0)>0).reduce((s,d)=>s+(d.total_revenue||0),0);
            const cyRev = dailyRevCY > 0 ? dailyRevCY : (row?.actual_revenue || null);
            const lyRev = dailyRevLYM > 0 ? dailyRevLYM : (rowLY?.actual_revenue || null);
            const vsLYEur  = cyRev && lyRev ? Math.round(cyRev - lyRev) : null;
            const cyCovers = row?.actual_covers || null;
            const lyCovers = rowLY?.actual_covers || null;
            const cyHours = (row?.kitchen_hours||0) + (row?.foh_hours||0) || null;
            const cyRevHr = cyRev && cyHours ? Math.round(cyRev / cyHours) : null;
            const isCurrentMonth = monthNum===new Date().getMonth()+1&&year===CURRENT_YEAR;
            const isFuture = year===CURRENT_YEAR && i>new Date().getMonth();
            return (
              <tr key={i} onClick={() => !isFuture && setModalMonth(i + 1)} style={{ borderBottom:'1px solid #f8fafc', background:isCurrentMonth?'#f0fdf4':isFuture?'#fafafa':i%2===0?'#fff':'#fafafa', cursor: isFuture ? 'default' : 'pointer' }}>
                <td style={{ padding:'10px 12px', fontSize:'13px', fontWeight:isCurrentMonth?700:400, color:isFuture?'#94a3b8':'inherit' }}>{m}{isCurrentMonth&&<span style={{ fontSize:'10px', color:'#10b981', marginLeft:'4px' }}>●</span>}</td>
                <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:isFuture?400:700, fontSize:'13px', fontStyle:isFuture?'italic':'normal', color:isFuture?'#94a3b8':'#0f172a' }}>
                  {isFuture ? '—' : (cyRev ? fmt(cyRev) : '—')}
                </td>
                <td style={{ padding:'10px 12px', textAlign:'right', fontSize:'12px', fontWeight:600, color:vsLYEur!==null?(vsLYEur>=0?'#10b981':'#ef4444'):'#cbd5e1' }}>
                  {vsLYEur!==null ? <span>{vsLYEur>=0?'▲':'▼'} {fmt(Math.abs(vsLYEur))}</span> : '—'}
                </td>
                <td style={{ padding:'10px 12px', textAlign:'right', fontSize:'12px', color:isFuture?'#cbd5e1':'#64748b' }}>{cyCovers ? fmtNum(cyCovers) : '—'}</td>
                <td style={{ padding:'10px 12px', textAlign:'right', fontSize:'12px', color:isFuture?'#cbd5e1':'#64748b' }}>{cyRevHr ? fmt(cyRevHr) : '—'}</td>
                <td style={{ padding:'10px 8px', textAlign:'right', fontSize:'14px', color: isFuture ? 'transparent' : '#94a3b8' }}>›</td>
              </tr>
            );
          })}
        </tbody>
        {/* Full Year totals row */}
        {(() => {
          const completedMonths = MONTHS.map((m,i) => {
            const monthNum = i + 1;
            const row   = monthly.find(r=>r.month===monthNum);
            const rowLY = monthlyLY.find(r=>r.month===monthNum);
            const isFuture = year===CURRENT_YEAR && i>new Date().getMonth();
            if (isFuture) return null;
            const dailyRevCY = dailyData.filter(d=>(venue==='Group'||d.venue===venue)&&new Date(d.date).getMonth()+1===monthNum&&(d.total_revenue||0)>0).reduce((s,d)=>s+(d.total_revenue||0),0);
            const dailyRevLYM = dailyDataLY.filter(d=>(venue==='Group'||d.venue===venue)&&new Date(d.date).getMonth()+1===monthNum&&(d.total_revenue||0)>0).reduce((s,d)=>s+(d.total_revenue||0),0);
            const cyRev = dailyRevCY > 0 ? dailyRevCY : (row?.actual_revenue || null);
            const lyRev = dailyRevLYM > 0 ? dailyRevLYM : (rowLY?.actual_revenue || null);
            return { cyRev, lyRev, cyCovers: row?.actual_covers || 0, lyCovers: rowLY?.actual_covers || 0, cyHours: (row?.kitchen_hours||0)+(row?.foh_hours||0) };
          }).filter(Boolean);
          const totalCY  = completedMonths.reduce((s,m) => s + (m.cyRev || 0), 0) || null;
          const totalLY  = completedMonths.reduce((s,m) => s + (m.lyRev || 0), 0) || null;
          const totalCovers = completedMonths.reduce((s,m) => s + m.cyCovers, 0) || null;
          const totalVsLY = totalCY && totalLY ? Math.round(totalCY - totalLY) : null;
          const totalHours = completedMonths.reduce((s,m) => s + m.cyHours, 0) || null;
          const totalRevHr = totalCY && totalHours ? Math.round(totalCY / totalHours) : null;
          if (!totalCY) return null;
          return (
            <tfoot>
              <tr style={{ borderTop:'2px solid #e2e8f0', background:'#f0f4f8' }}>
                <td style={{ padding:'11px 12px', fontSize:'13px', fontWeight:800, color:'#0f172a' }}>Full Year</td>
                <td style={{ padding:'11px 12px', textAlign:'right', fontWeight:800, fontSize:'13px', color:'#0f172a' }}>{fmt(totalCY)}</td>
                <td style={{ padding:'11px 12px', textAlign:'right', fontSize:'12px', fontWeight:700, color:totalVsLY!=null?(totalVsLY>=0?'#10b981':'#ef4444'):'#cbd5e1' }}>
                  {totalVsLY!=null ? <span>{totalVsLY>=0?'▲':'▼'} {fmt(Math.abs(totalVsLY))}</span> : '—'}
                </td>
                <td style={{ padding:'11px 12px', textAlign:'right', fontSize:'12px', fontWeight:700, color:'#0f172a' }}>{totalCovers ? fmtNum(totalCovers) : '—'}</td>
                <td style={{ padding:'11px 12px', textAlign:'right', fontSize:'12px', fontWeight:700, color:'#0f172a' }}>{totalRevHr ? fmt(totalRevHr) : '—'}</td>
                <td/>
              </tr>
            </tfoot>
          );
        })()}
      </table>
    </div>
  );

  const Charts = () => (
    <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

      {/* Revenue */}
      {(()=>{ const domain=['auto','auto']; return (
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Revenue</div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#3A8FA3', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#BCD9E5', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/><XAxis dataKey="month" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} domain={domain} width={50}/><Tooltip formatter={fmt}/>
              <Line type="monotone" dataKey="Revenue" stroke="#3A8FA3" strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="Revenue LY" stroke="#BCD9E5" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      ); })()}

      {/* Hours */}
      {hasHours && (
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Hours</div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#E4AC49', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#C4860A', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/><XAxis dataKey="month" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} width={40} domain={yDomain(monthlyChartData,['Hours','Hours LY'],0.2)}/><Tooltip formatter={v => v != null ? v+'h' : '—'}/>
              <Line type="monotone" dataKey="Hours" stroke="#E4AC49" strokeWidth={2} dot={false} connectNulls/>
              <Line type="monotone" dataKey="Hours LY" stroke="#C4860A" strokeWidth={1.5} strokeDasharray="4 2" dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Rev/Hr — repositioned above Covers */}
      {monthlyChartData.some(d => d['Rev/Hr'] != null) && (()=>{ const domain=yDomain(monthlyChartData,['Rev/Hr','Rev/Hr LY'],0.15); return (
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Revenue per Hour Worked</div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#7C3AED', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#c4b5fd', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/><XAxis dataKey="month" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`€${v}`} domain={domain} width={45}/><Tooltip formatter={fmt}/>
              <Line type="monotone" dataKey="Rev/Hr" stroke="#7C3AED" strokeWidth={2} dot={false} connectNulls/>
              <Line type="monotone" dataKey="Rev/Hr LY" stroke="#c4b5fd" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      ); })()}

      {/* Covers — with hours overlay toggle */}
      <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', flex:1 }}>Covers</div>
          {hasHours && (
            <button onClick={() => setShowHoursOnCovers(v => !v)} style={{
              fontSize:'10px', fontWeight:600, padding:'3px 9px', borderRadius:'20px', border:'none', cursor:'pointer',
              background: showHoursOnCovers ? '#FDF3DC' : '#f1f5f9',
              color: showHoursOnCovers ? '#E4AC49' : '#94a3b8',
              transition:'all 0.15s',
            }}>
              {showHoursOnCovers ? '◉ Hours on' : '○ + Hours'}
            </button>
          )}
        </div>
        <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px', flexWrap:'wrap' }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#6366f1', display:'inline-block', borderRadius:2 }}/>{year} Covers</span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#a5b4fc', display:'inline-block', borderRadius:2 }}/>{year-1} Covers</span>
          {hasCoversHours && <>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#E4AC49', display:'inline-block', borderRadius:2 }}/>{year} Hours</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#C4860A', display:'inline-block', borderRadius:2 }}/>{year-1} Hours</span>
          </>}
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={monthlyChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="month" tick={{fontSize:10}}/>
            <YAxis yAxisId="covers" tick={{fontSize:10}} width={40}/>
            {hasCoversHours && <YAxis yAxisId="hours" orientation="right" tick={{fontSize:10}} width={35} tickFormatter={v=>v+'h'}/>}
            <Tooltip formatter={(val, name) => name.includes('Hours') ? (val != null ? val+'h' : '—') : fmtNum(val)}/>
            <Line yAxisId="covers" type="monotone" dataKey="Covers" stroke="#6366f1" strokeWidth={2} dot={false} connectNulls/>
            <Line yAxisId="covers" type="monotone" dataKey="Covers LY" stroke="#a5b4fc" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            {hasCoversHours && <>
              <Line yAxisId="hours" type="monotone" dataKey="Hours" stroke="#E4AC49" strokeWidth={2} dot={false} connectNulls/>
              <Line yAxisId="hours" type="monotone" dataKey="Hours LY" stroke="#C4860A" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            </>}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Avg Check */}
      {(()=>{ const domain=yDomain(monthlyChartData,['Avg Check','Avg Check LY'],0.15); return (
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontWeight:700, fontSize:'13px', color:'#0f172a', marginBottom:'4px' }}>Avg Check</div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'12px', display:'flex', gap:'12px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#10b981', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:12, height:3, background:'#6ee7b7', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={monthlyChartData}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/><XAxis dataKey="month" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={v=>`€${v}`} domain={domain} width={40}/><Tooltip formatter={fmt}/>
              <Line type="monotone" dataKey="Avg Check" stroke="#10b981" strokeWidth={2} dot={false} connectNulls/>
              <Line type="monotone" dataKey="Avg Check LY" stroke="#6ee7b7" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      ); })()}
    </div>
  );

  // Mobile: charts first, then table. Desktop: side-by-side grid.
  if (isMobile) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
        {modalMonth && <MonthModal monthNum={modalMonth} monthly={monthly} monthlyLY={monthlyLY} dailyData={dailyData} dailyDataLY={dailyDataLY} venue={venue} year={year} onClose={() => setModalMonth(null)}/>}
        <Charts/>
        <MonthlyTable/>
      </div>
    );
  }

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1.5fr', gap:'24px', alignItems:'start' }}>
      {modalMonth && <MonthModal monthNum={modalMonth} monthly={monthly} monthlyLY={monthlyLY} dailyData={dailyData} dailyDataLY={dailyDataLY} venue={venue} year={year} onClose={() => setModalMonth(null)}/>}
      <MonthlyTable/>
      <Charts/>
    </div>
  );
}

// ── Mobile Hours/Productivity Toggle Card ─────────────────────────────────────
function MobileHoursToggle({ chartData16, year }) {
  const [mode, setMode] = useState('hours');
  const hasRevHr = chartData16.some(d => d['Rev/Hr'] != null);
  return (
    <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'14px 16px', margin:'0 16px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
        <span style={{ fontWeight:700, fontSize:'13px', color:'#0f172a' }}>
          {mode === 'hours' ? 'Hours Trend' : 'Revenue per Hour'}
        </span>
        {hasRevHr && (
          <div style={{ display:'flex', gap:'4px', background:'#f1f5f9', borderRadius:'20px', padding:'2px' }}>
            <button onClick={() => setMode('hours')}
              style={{ padding:'3px 10px', borderRadius:'20px', border:'none', cursor:'pointer', fontSize:'11px', fontWeight:600, background:mode==='hours'?'#1e293b':'transparent', color:mode==='hours'?'#fff':'#64748b', transition:'all 0.15s' }}>
              Hours
            </button>
            <button onClick={() => setMode('productivity')}
              style={{ padding:'3px 10px', borderRadius:'20px', border:'none', cursor:'pointer', fontSize:'11px', fontWeight:600, background:mode==='productivity'?'#7C3AED':'transparent', color:mode==='productivity'?'#fff':'#64748b', transition:'all 0.15s' }}>
              Productivity
            </button>
          </div>
        )}
      </div>
      {mode === 'hours' ? (
        <>
          <div style={{ display:'flex', gap:'10px', marginBottom:'8px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'#64748b' }}><span style={{ width:14, height:2, background:'#E4AC49', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'#64748b' }}><span style={{ width:14, height:2, background:'#C4860A', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width='100%' height={120}>
            <LineChart data={chartData16} margin={{ top:4, right:8, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/>
              <XAxis dataKey='week' tick={{fontSize:9, fill:'#94a3b8'}} interval='preserveStartEnd'/>
              <YAxis tick={{fontSize:10, fill:'#94a3b8'}} width={32}/>
              <Tooltip formatter={v => v != null ? v+'h' : '—'} labelStyle={{fontSize:'11px'}} contentStyle={{fontSize:'12px'}}/>
              <Line type='monotone' dataKey='Hours' stroke='#E4AC49' strokeWidth={2.5} dot={false} connectNulls/>
              <Line type='monotone' dataKey='Hours LY' stroke='#C4860A' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </>
      ) : (
        <>
          <div style={{ display:'flex', gap:'10px', marginBottom:'8px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'#64748b' }}><span style={{ width:14, height:2, background:'#7C3AED', display:'inline-block', borderRadius:2 }}/>{year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'#64748b' }}><span style={{ width:14, height:2, background:'#c4b5fd', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
          </div>
          <ResponsiveContainer width='100%' height={120}>
            <LineChart data={chartData16} margin={{ top:4, right:8, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/>
              <XAxis dataKey='week' tick={{fontSize:9, fill:'#94a3b8'}} interval='preserveStartEnd'/>
              <YAxis tick={{fontSize:10, fill:'#94a3b8'}} tickFormatter={v=>`€${v}`} width={40}/>
              <Tooltip formatter={fmt} labelStyle={{fontSize:'11px'}} contentStyle={{fontSize:'12px'}}/>
              <Line type='monotone' dataKey='Rev/Hr' stroke='#7C3AED' strokeWidth={2.5} dot={false} connectNulls/>
              <Line type='monotone' dataKey='Rev/Hr LY' stroke='#c4b5fd' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN RESULTS COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function Results({ currentUser }) {
  const isMobile = useIsMobile();

  const isAdmin       = isAdminUser(currentUser);
  const isManager     = currentUser?.role === 'manager' || isAdmin || currentUser?.role === 'advisor';
  const allowedVenues = isAdmin
    ? VENUES
    : (currentUser?.venues?.length ? currentUser.venues : [currentUser?.venue])
        .filter(v => VENUES.includes(v));

  const [tab, setTab]     = useState(() => {
    try {
      const intent = localStorage.getItem('results_intent_tab');
      if (intent) { localStorage.removeItem('results_intent_tab'); return intent; }
    } catch {}
    return 'weekly';
  });
  const [venue, setVenue] = useState(() => {
    try {
      const intent = localStorage.getItem('results_intent_venue');
      if (intent && allowedVenues.includes(intent)) {
        localStorage.removeItem('results_intent_venue');
        return intent;
      }
    } catch {}
    return allowedVenues[0] ?? 'Box Jordaan';
  });
  const [year, setYear]   = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState(null);
  const [costsSubTab, setCostsSubTab] = useState('payroll');

  const [results, setResults]       = useState([]);
  const [resultsLY, setResultsLY]   = useState([]);
  const [comments, setComments]     = useState([]);
  const [modalRow, setModalRow]     = useState(null);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [monthly, setMonthly]       = useState([]);
  const [monthlyLY, setMonthlyLY]   = useState([]);
  const [dailyData, setDailyData]   = useState([]);
  const [dailyDataLY, setDailyDataLY] = useState([]);
  const [views, setViews] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    if (venue === 'Group') {
      const [cyResults, lyResults, mResults, mLYResults, dResults, dResultsLY, cData, viewsData] = await Promise.all([
        Promise.all(ALL_VENUES.map(v => supabase.from('results').select('*').eq('venue', v).eq('year', year).order('week'))),
        Promise.all(ALL_VENUES.map(v => supabase.from('results').select('*').eq('venue', v).eq('year', year - 1).order('week'))),
        Promise.all(ALL_VENUES.map(v => supabase.from('results_monthly').select('*').eq('venue', v).eq('year', year).order('month'))),
        Promise.all(ALL_VENUES.map(v => supabase.from('results_monthly').select('*').eq('venue', v).eq('year', year - 1).order('month'))),
        Promise.all(ALL_VENUES.map(v => supabase.from('results_daily').select('*').eq('venue', v).eq('year', year))),
        Promise.all(ALL_VENUES.map(v => supabase.from('results_daily').select('*').eq('venue', v).eq('year', year - 1))),
        supabase.from('results_comments').select('*').order('created_at'),
        supabase.from('result_views').select('*'),
      ]);
      const aggWeekly = (venueResults) => {
        const map = {};
        venueResults.forEach(({ data }) => (data || []).forEach(r => {
          const k = r.week;
          if (!map[k]) map[k] = { ...r, venue: 'Group', brunch_revenue: 0, dinner_revenue: 0, total_covers: 0, total_hours: 0 };
          map[k].brunch_revenue += r.brunch_revenue || 0;
          map[k].dinner_revenue += r.dinner_revenue || 0;
          map[k].total_covers   += r.total_covers   || 0;
          map[k].total_hours    += r.total_hours     || 0;
        }));
        return Object.values(map).sort((a, b) => a.week - b.week);
      };
      const aggMonthly = (venueResults) => {
        const map = {};
        const fields = ['actual_revenue','budget_revenue','actual_covers','budget_covers','food_cost','bev_cost',
          'kitchen_payroll','foh_payroll','lm_prep_payroll_alloc','lm_transfers_alloc','group_costs','other_opex',
          'budget_kitchen_payroll','budget_foh_payroll','budget_food_cost','budget_bev_cost',
          'kitchen_hours','foh_hours','budget_kitchen_hours','budget_foh_hours','food_revenue','bev_revenue'];
        venueResults.forEach(({ data }) => (data || []).forEach(r => {
          const k = r.month;
          if (!map[k]) { map[k] = { month: k }; fields.forEach(f => map[k][f] = 0); }
          fields.forEach(f => { map[k][f] += r[f] || 0; });
        }));
        return Object.values(map).sort((a, b) => a.month - b.month);
      };
      setResults(aggWeekly(cyResults));
      setResultsLY(aggWeekly(lyResults));
      setMonthly(aggMonthly(mResults));
      setMonthlyLY(aggMonthly(mLYResults));
      setDailyData(dResults.flatMap(({ data }) => data || []));
      setDailyDataLY(dResultsLY.flatMap(({ data }) => data || []));
      if (cData.data) setComments(cData.data);
      if (viewsData.data) setViews(viewsData.data);
    } else {
      const [{ data: rData }, { data: rLY }, { data: cData }, { data: mData }, { data: mLY }, { data: dData }, { data: dDataLY }, { data: viewsData }] = await Promise.all([
        supabase.from('results').select('*').eq('venue', venue).eq('year', year).order('week'),
        supabase.from('results').select('*').eq('venue', venue).eq('year', year - 1).order('week'),
        supabase.from('results_comments').select('*').order('created_at'),
        supabase.from('results_monthly').select('*').eq('venue', venue).eq('year', year).order('month'),
        supabase.from('results_monthly').select('*').eq('venue', venue).eq('year', year - 1).order('month'),
        supabase.from('results_daily').select('*').eq('year', year),
        supabase.from('results_daily').select('*').eq('year', year - 1),
        supabase.from('result_views').select('*'),
      ]);
      if (rData) setResults(rData);
      if (rLY)   setResultsLY(rLY);
      if (cData) setComments(cData);
      if (mData) setMonthly(mData);
      if (mLY)   setMonthlyLY(mLY);
      if (dData)   setDailyData(dData);
      if (dDataLY) setDailyDataLY(dDataLY);
      if (viewsData) setViews(viewsData);
    }
    setLoading(false);
  }, [venue, year]);

  useEffect(() => { load(); }, [load]);

  // Helper: sum dailyData revenue for a given week+year+venue
  function dailyWeekRevenue(daily, weekNum, weekYear, v) {
    return daily
      .filter(d => {
        const dt = new Date(d.date);
        return getWeekNumber(dt) === weekNum
          && dt.getFullYear() === weekYear
          && (v === 'Group' || d.venue === v)
          && (d.total_revenue || 0) > 0;
      })
      .reduce((s, d) => s + (d.total_revenue || 0), 0);
  }

  // Helper: sum dailyData covers for a given week — only days where covers > 0
  function dailyWeekCovers(daily, weekNum, weekYear, v) {
    return daily
      .filter(d => {
        const dt = new Date(d.date);
        return getWeekNumber(dt) === weekNum
          && dt.getFullYear() === weekYear
          && (v === 'Group' || d.venue === v)
          && (d.total_covers || 0) > 0;
      })
      .reduce((s, d) => s + (d.total_covers || 0), 0);
  }

  // Helper: sum dailyData revenue ONLY for days that also have covers > 0
  // Used as the numerator for avg check, so we don't divide full-week revenue by partial-week covers.
  function dailyWeekRevenueWithCovers(daily, weekNum, weekYear, v) {
    return daily
      .filter(d => {
        const dt = new Date(d.date);
        return getWeekNumber(dt) === weekNum
          && dt.getFullYear() === weekYear
          && (v === 'Group' || d.venue === v)
          && (d.total_covers || 0) > 0
          && (d.total_revenue || 0) > 0;
      })
      .reduce((s, d) => s + (d.total_revenue || 0), 0);
  }

  const enrich = (rows, daily, v) => rows.map(r => {
    const dailyRev = dailyWeekRevenue(daily, r.week, r.year, v);
    const fallback = (r.brunch_revenue || 0) + (r.dinner_revenue || 0);
    const total_revenue = dailyRev > 0 ? dailyRev : fallback;

    // Covers: prefer daily sum (only days with covers > 0); fall back to weekly results.total_covers
    const dailyCovers   = dailyWeekCovers(daily, r.week, r.year, v);
    const total_covers  = dailyCovers > 0 ? dailyCovers : (r.total_covers || 0);

    // Revenue used for avg check: only days that also had covers entered
    const revWithCovers = dailyWeekRevenueWithCovers(daily, r.week, r.year, v);
    // If no daily covers anywhere in the week, fall back to weekly logic (total_revenue / weekly total_covers)
    const avgRevSource  = dailyCovers > 0 ? revWithCovers : total_revenue;

    return {
      ...r,
      total_revenue,
      total_covers,
      rev_per_cover: total_covers > 0 && avgRevSource > 0 ? avgRevSource / total_covers : 0,
      _avg_rev_source: avgRevSource,  // exposed for KPI calcs that need it
    };
  });

  // Weeks that exist in dailyData but have no results row yet (e.g. new week just synced from Eitje)
  const dailyOnlyWeeks = [...new Set(
    dailyData
      .filter(d => venue === 'Group' || d.venue === venue)
      .map(d => getWeekNumber(new Date(d.date)))
  )]
  .filter(w => !results.find(r => r.week === w))
  .map(w => ({
    week: w, year, venue,
    total_revenue: dailyWeekRevenue(dailyData, w, year, venue),
    total_covers: 0, dinner_revenue: 0, brunch_revenue: 0,
    total_hours: 0, kitchen_hours: 0, foh_hours: 0,
    rev_per_cover: 0, _avg_rev_source: 0,
  }));

  const enriched   = [...enrich(results, dailyData, venue), ...dailyOnlyWeeks].sort((a, b) => a.week - b.week);
  const enrichedLY = enrich(resultsLY, dailyDataLY, venue);

  const availableMonths = [...new Set(enriched.map(r => weekToMonthIndex(r.week, r.year) + 1))].sort((a, b) => a - b);

  function toggleMonth(m) {
    if (selectedMonths === null) { setSelectedMonths([m]); return; }
    const next = selectedMonths.includes(m) ? selectedMonths.filter(x => x !== m) : [...selectedMonths, m];
    setSelectedMonths(next.length === 0 ? null : next);
  }

  const tableWeeksAsc = selectedMonths === null ? enriched : enriched.filter(r => selectedMonths.includes(weekToMonthIndex(r.week, r.year) + 1));
  const tableWeeks = [...tableWeeksAsc].reverse();

  const cmIdx   = new Date().getMonth();
  const cmMonth = cmIdx + 1;
  const cmData  = monthly.find(m => m.month === cmMonth);
  const cmLY    = monthlyLY.find(m => m.month === cmMonth);

  const dailyThisMonth = dailyData.filter(d => {
    const dt = new Date(d.date);
    return (venue === 'Group' || d.venue === venue) && dt.getFullYear() === year && dt.getMonth() === cmIdx;
  });

  // MTD revenue = sum of daily rows with revenue > 0
  const mtdDailyRows  = dailyThisMonth.filter(d => (d.total_revenue || 0) > 0);
  const mtdRevenue    = mtdDailyRows.reduce((s, d) => s + (d.total_revenue || 0), 0) || null;

  // Most recent day with revenue > 0 — used for LY comparison
  const mtdLatestDate = mtdDailyRows.length > 0
    ? new Date(mtdDailyRows.map(d => d.date).sort().reverse()[0])
    : null;
  const mtdDayOfMonth = mtdLatestDate ? mtdLatestDate.getDate() : null;

  // LY MTD: sum dailyDataLY from 1st → same day of month
  const lyMtdRevenue = mtdDayOfMonth ? dailyDataLY.filter(d => {
    const dt = new Date(d.date);
    return (venue === 'Group' || d.venue === venue)
      && dt.getFullYear() === year - 1
      && dt.getMonth() === cmIdx
      && dt.getDate() <= mtdDayOfMonth
      && (d.total_revenue || 0) > 0;
  }).reduce((s, d) => s + (d.total_revenue || 0), 0) || null : null;

  const weeksThisMonth = enriched.filter(r => weekToMonthIndex(r.week, r.year) === cmIdx);
  const mostRecentWeek = weeksThisMonth.length > 0 ? weeksThisMonth.reduce((a, b) => a.week > b.week ? a : b) : null;

  // MTD covers — from results (manual entry), sum weeks in current month
  const mtdCovers = weeksThisMonth.reduce((s, r) => s + (r.total_covers || 0), 0) || null;
  // LY covers for same weeks
  const lyMtdCovers = enrichedLY.filter(r => weekToMonthIndex(r.week, r.year) === cmIdx)
    .reduce((s, r) => s + (r.total_covers || 0), 0) || null;

  let proRateFraction = 1, proRateLabel = null, proRateDays = null, proRateTotalDays = null;
  const totalDays  = daysInMonth(year, cmMonth);
  const monthStart = new Date(year, cmIdx, 1);

  if (mtdLatestDate) {
    const reportedDays  = Math.round((mtdLatestDate - monthStart) / 86400000) + 1;
    proRateFraction  = Math.min(reportedDays / totalDays, 1);
    proRateDays      = reportedDays;
    proRateTotalDays = totalDays;
    const dayLabel   = mtdLatestDate.getDate() + ' ' + MONTHS[mtdLatestDate.getMonth()];
    proRateLabel     = `Pro-rated to ${dayLabel} (${reportedDays}/${totalDays} days)`;
  } else if (mostRecentWeek) {
    const weekEnd      = weekEndDate(mostRecentWeek.week, year);
    const reportedTo   = weekEnd > new Date(year, cmMonth, 0) ? new Date(year, cmMonth, 0) : weekEnd;
    const reportedDays = Math.round((reportedTo - monthStart) / 86400000) + 1;
    proRateFraction  = Math.min(reportedDays / totalDays, 1);
    proRateDays      = reportedDays;
    proRateTotalDays = totalDays;
    proRateLabel     = `Pro-rated to W${mostRecentWeek.week} (${reportedDays}/${totalDays} days)`;
  }

  // Rev/cover: only include weeks where covers > 0
  const mtdRevForAvg = weeksThisMonth.filter(r => (r.total_covers||0) > 0).reduce((s,r) => s + (r._avg_rev_source ?? r.total_revenue), 0) || null;
  const mtdAvgCheck  = mtdCovers && mtdRevForAvg ? mtdRevForAvg / mtdCovers : null;
  const lyRevForAvg  = enrichedLY.filter(r => weekToMonthIndex(r.week, r.year) === cmIdx && (r.total_covers||0) > 0).reduce((s,r) => s + (r._avg_rev_source ?? r.total_revenue), 0) || null;
  const lyAvgCheck   = lyMtdCovers && lyRevForAvg ? lyRevForAvg / lyMtdCovers : null;

  const summaryCards = [
    { label:`${MONTHS[cmIdx]} Revenue`,   actual: mtdRevenue, budget: cmData?.budget_revenue ? cmData.budget_revenue * proRateFraction : null, ly: lyMtdRevenue, fmtFn: fmt, proRateLabel },
    { label:`${MONTHS[cmIdx]} Covers`,    actual: mtdCovers,  budget: cmData?.budget_covers  ? cmData.budget_covers  * proRateFraction : null, ly: lyMtdCovers,  fmtFn: fmtNum, proRateLabel },
    { label:`${MONTHS[cmIdx]} Avg Check`, actual: mtdAvgCheck, budget: cmData?.budget_covers ? cmData.budget_revenue / cmData.budget_covers : null, ly: lyAvgCheck, fmtFn: fmt, proRateLabel: null },
  ];

  // Week is complete only if Sunday daily data exists for it
  const completedWeeks = new Set(
    enriched.map(r => r.week).filter(w => {
      const jan4 = new Date(year, 0, 4);
      const startW1 = new Date(jan4);
      startW1.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
      const sunday = new Date(startW1);
      sunday.setDate(startW1.getDate() + (w - 1) * 7 + 6);
      const sundayStr = sunday.getFullYear() + '-' + String(sunday.getMonth()+1).padStart(2,'0') + '-' + String(sunday.getDate()).padStart(2,'0');
      return dailyData.some(d => d.date === sundayStr && (venue === 'Group' || d.venue === venue));
    })
  );

  // Also accept weeks whose Sunday is in the past (fallback when no daily data uploaded)
  const currentWeekNum = getWeekNumber(new Date());
  const isWeekComplete = (w) => {
    if (year < CURRENT_YEAR) return true; // prior year — all weeks complete
    if (completedWeeks.has(w)) return true;
    // Sunday of week w is in the past
    const jan4 = new Date(year, 0, 4);
    const startW1 = new Date(jan4);
    startW1.setDate(jan4.getDate() - (jan4.getDay() + 6) % 7);
    const sunday = new Date(startW1);
    sunday.setDate(startW1.getDate() + (w - 1) * 7 + 6);
    sunday.setHours(23, 59, 59, 0);
    return sunday < new Date();
  };

  // Month is complete if it's before the current month (current year) or any month of a prior year
  const currentMonthNum = new Date().getMonth() + 1;
  const isMonthComplete = (monthNum) => {
    if (year < CURRENT_YEAR) return true;
    return monthNum < currentMonthNum;
  };

  // Top 4 week ranking — rank each completed week against all completed CY weeks by revenue
  const completedWeekRevs = enriched
    .filter(r => isWeekComplete(r.week))
    .map(r => r.total_revenue)
    .sort((a, b) => b - a);
  const getWeekRank = (rev) => {
    if (!rev) return null;
    const rank = completedWeekRevs.findIndex(v => v <= rev) + 1;
    return rank >= 1 && rank <= 3 ? rank : null;
  };

  const allWeeks = [...new Set([...enriched.map(r=>r.week), ...enrichedLY.map(r=>r.week)])].sort((a,b)=>a-b);
  const chartData = allWeeks.map(w => {
    const cy = enriched.find(r=>r.week===w);
    const ly = enrichedLY.find(r=>r.week===w);
    // Only include CY data for complete weeks; always include LY for context
    const cyComplete = cy && isWeekComplete(w);
    return {
      week:`W${w}`, weekNum:w,
      'Revenue':cyComplete?cy.total_revenue:null, 'Revenue LY':ly?ly.total_revenue:null,
      'Covers':cyComplete?cy.total_covers:null, 'Covers LY':ly?ly.total_covers:null,
      'Rev/Cover':cyComplete?Math.round(cy.rev_per_cover*100)/100:null,
      'Rev/Cover LY':ly?Math.round(ly.rev_per_cover*100)/100:null,
      'Rev/Hr':cyComplete&&cy.total_hours>0?Math.round(cy.total_revenue/cy.total_hours):null,
      'Rev/Hr LY':ly?.total_hours>0?Math.round(ly.total_revenue/ly.total_hours):null,
      'Brunch':cyComplete?Math.max(0,(cy.total_revenue||0)-(cy.dinner_revenue||0)):null, 'Dinner':cyComplete?cy.dinner_revenue:null,
      'Hours':cyComplete&&cy.total_hours>0?Math.round(cy.total_hours):null,
      'Hours LY':ly&&ly.total_hours>0?Math.round(ly.total_hours):null,
    };
  }).filter(d => d['Revenue'] !== null || d['Revenue LY'] !== null); // drop rows with no data at all

  // Last 8 completed CY weeks — for desktop sub-charts
  const chartData8 = chartData.filter(d=>d['Revenue']!==null).sort((a,b)=>b.weekNum-a.weekNum).slice(0,8).sort((a,b)=>a.weekNum-b.weekNum);

  // Last 16 completed CY weeks + matching LY weeks — for mobile trend chart
  const last16CYWeeks = chartData
    .filter(d => d['Revenue'] !== null)
    .sort((a,b) => b.weekNum - a.weekNum)
    .slice(0, 16)
    .sort((a,b) => a.weekNum - b.weekNum);
  const chartData16 = last16CYWeeks.map(d => ({
    ...d,
    'Revenue LY': enrichedLY.find(r => r.week === d.weekNum)?.total_revenue ?? null,
    'Hours LY': enrichedLY.find(r => r.week === d.weekNum)?.total_hours > 0
      ? Math.round(enrichedLY.find(r => r.week === d.weekNum).total_hours) : null,
  }));

  const monthlyChartData = MONTHS.map((m,i) => {
    const cy = monthly.find(r=>r.month===i+1);
    const ly = monthlyLY.find(r=>r.month===i+1);
    const complete = isMonthComplete(i+1);
    const monthNum = i + 1;

    // Revenue from dailyData (primary) — fallback to results_monthly for 2024/no-daily months
    const dailyRevCY = dailyData.filter(d => {
      const dt = new Date(d.date);
      return (venue === 'Group' || d.venue === venue) && dt.getFullYear() === year && dt.getMonth() === i && (d.total_revenue||0) > 0;
    }).reduce((s,d) => s + (d.total_revenue||0), 0);
    const dailyRevLY = dailyDataLY.filter(d => {
      const dt = new Date(d.date);
      return (venue === 'Group' || d.venue === venue) && dt.getFullYear() === year-1 && dt.getMonth() === i && (d.total_revenue||0) > 0;
    }).reduce((s,d) => s + (d.total_revenue||0), 0);

    const cyRev = complete ? (dailyRevCY > 0 ? dailyRevCY : (cy?.actual_revenue||null)) : null;
    const lyRev = dailyRevLY > 0 ? dailyRevLY : (ly?.actual_revenue||null);

    // Covers from enriched weekly rows
    const cyCovers = complete ? (enriched.filter(r => weekToMonthIndex(r.week,r.year) === i).reduce((s,r) => s+(r.total_covers||0),0) || (cy?.actual_covers||null)) : null;
    const lyCovers = enrichedLY.filter(r => weekToMonthIndex(r.week,r.year) === i).reduce((s,r) => s+(r.total_covers||0),0) || (ly?.actual_covers||null);

    // Avg check — only weeks with covers > 0; use revenue from days with covers (not full week revenue)
    const cyRevForAvg = complete ? enriched.filter(r => weekToMonthIndex(r.week,r.year) === i && (r.total_covers||0) > 0).reduce((s,r) => s+(r._avg_rev_source ?? r.total_revenue),0) : 0;
    const lyRevForAvg = enrichedLY.filter(r => weekToMonthIndex(r.week,r.year) === i && (r.total_covers||0) > 0).reduce((s,r) => s+(r._avg_rev_source ?? r.total_revenue),0);

    return {
      month: m,
      'Revenue': cyRev,
      'Revenue LY': lyRev,
      'Budget': cy?.budget_revenue || null,
      'Covers': cyCovers,
      'Covers LY': lyCovers,
      'Budget Covers': cy?.budget_covers || null,
      'Avg Check': cyCovers && cyRevForAvg ? Math.round(cyRevForAvg/cyCovers) : null,
      'Avg Check LY': lyCovers && lyRevForAvg ? Math.round(lyRevForAvg/lyCovers) : null,
      'Budget Avg Check': cy?.budget_covers && cy?.budget_revenue ? Math.round(cy.budget_revenue/cy.budget_covers) : null,
      'Hours':    complete && cy ? (cy.kitchen_hours||0)+(cy.foh_hours||0)||null : null,
      'Hours LY': ly ? (ly.kitchen_hours||0)+(ly.foh_hours||0)||null : null,
      'Kitchen Hours':    complete && cy ? cy.kitchen_hours||null : null,
      'FOH Hours':        complete && cy ? cy.foh_hours||null    : null,
      'Kitchen Hours LY': ly ? ly.kitchen_hours||null : null,
      'FOH Hours LY':     ly ? ly.foh_hours||null     : null,
      'Rev/Hr':    (() => { const h=(cy?.kitchen_hours||0)+(cy?.foh_hours||0); const rev=complete?(dailyRevCY>0?dailyRevCY:(cy?.actual_revenue||null)):null; return h>0&&rev?Math.round(rev/h):null; })(),
      'Rev/Hr LY': (() => { const h=(ly?.kitchen_hours||0)+(ly?.foh_hours||0); const rev=dailyRevLY>0?dailyRevLY:(ly?.actual_revenue||null); return h>0&&rev?Math.round(rev/h):null; })(),
    };
  });

  const handleView = async (resultId) => {
    if (currentUser?.role === 'advisor') return;
    const alreadyViewed = views.some(v => v.result_id === resultId && v.user_email === currentUser?.email);
    if (alreadyViewed) return;
    const { data } = await supabase.from('result_views').insert([{
      result_id: resultId,
      user_email: currentUser?.email,
      user_name: currentUser?.name || currentUser?.email || 'Unknown',
    }]).select().single();
    if (data) setViews(prev => [...prev, data]);
  };

  const handlePostComment = async (resultId, directPayload) => {
    const text = directPayload || newComment;
    if (!text?.trim() || !isManager) return;
    setPostingComment(true);
    const { data } = await supabase.from('results_comments').insert([{ result_id:resultId, author_name:currentUser?.name||currentUser?.email||'Manager', author_email:currentUser?.email, comment:text.trim() }]).select().single();
    if (data) { setComments(prev=>[...prev,data]); if (!directPayload) setNewComment(''); }
    setPostingComment(false);
  };

  if (!isManager) return <div style={{ padding:'2rem', textAlign:'center', color:'#94a3b8' }}>Access restricted to managers and admins.</div>;

  const tabList = [
    { key:'weekly',  label:'Weekly'  },
    { key:'monthly', label:'Monthly' },
    { key:'costs',   label:'Costs'   },
  ];

  const isGroup = venue === 'Group';

  return (
    <div style={{ padding: isMobile ? '16px 0' : '24px', maxWidth:'1400px', fontFamily:'inherit' }}>

      <div style={{ padding: isMobile ? '0 16px' : '0', display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h2 style={{ margin:0, fontSize: isMobile ? '18px' : '22px', fontWeight:700, color:'#0f172a' }}>Results</h2>
          <p style={{ margin:'2px 0 0', fontSize:'13px', color:'#64748b' }}>Performance tracker — {venue}</p>
        </div>
      </div>

      {/* ── Tab strip + venue dropdown ── */}
      {isMobile ? (
        <>
          {/* Tab underline strip with venue dropdown on the right */}
          <div style={{
            display:'flex', alignItems:'center',
            borderBottom:'1px solid #e2e8f0',
            padding:'0 16px',
          }}>
            <div style={{ display:'flex', flex:1, overflowX:'auto', scrollbarWidth:'none', WebkitOverflowScrolling:'touch' }}>
              {tabList.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding:'8px 14px 9px', background:'none', border:'none',
                  cursor:'pointer', flexShrink:0,
                  borderBottom:`2.5px solid ${tab===t.key ? '#0f172a' : 'transparent'}`,
                  marginBottom:'-1px',
                  fontSize:'13px', fontWeight: tab===t.key ? 700 : 500,
                  color: tab===t.key ? '#0f172a' : '#64748b', whiteSpace:'nowrap',
                }}>
                  {t.label}
                </button>
              ))}
            </div>
            <div style={{ paddingLeft:'8px', paddingBottom:'2px' }}>
              <VenueDropdown venue={venue} allowedVenues={allowedVenues} isAdmin={isAdmin} onChange={v => setVenue(v)} />
            </div>
          </div>

          {/* Costs sub-tab strip */}
          {tab === 'costs' && (
            <div style={{
              display:'flex', overflowX:'auto', scrollbarWidth:'none', WebkitOverflowScrolling:'touch',
              borderBottom:'1px solid #e2e8f0', padding:'0 16px',
            }}>
              {[
                { key:'payroll', label:'Payroll' },
                { key:'food',    label:'Food' },
                ...(venue !== 'Sassys' ? [{ key:'bev', label:'Bev' }] : []),
              ].map(t => (
                <button key={t.key} onClick={() => setCostsSubTab(t.key)} style={{
                  padding:'7px 14px 8px', background:'none', border:'none',
                  cursor:'pointer', flexShrink:0,
                  borderBottom:`2.5px solid ${costsSubTab===t.key ? '#64748b' : 'transparent'}`,
                  marginBottom:'-1px',
                  fontSize:'12px', fontWeight: costsSubTab===t.key ? 700 : 400,
                  color: costsSubTab===t.key ? '#0f172a' : '#94a3b8', whiteSpace:'nowrap',
                }}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Desktop — existing pill row, keep year selector */
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'16px', alignItems:'center' }}>
          {allowedVenues.map(v=><button key={v} onClick={()=>setVenue(v)} style={pill(venue===v,true)}>{v}</button>)}
          {isAdmin && <button onClick={()=>setVenue('Group')} style={pill(venue==='Group',true)}>Group</button>}
          <div style={{ width:'1px', background:'#e2e8f0', margin:'0 4px' }}/>
          {YEARS.map(y=><button key={y} onClick={()=>setYear(y)} style={pill(year===y,true)}>{y}</button>)}
          <div style={{ width:'1px', background:'#e2e8f0', margin:'0 4px' }}/>
          {tabList.map(t=><button key={t.key} onClick={()=>setTab(t.key)} style={pill(tab===t.key,true)}>{t.label}</button>)}
        </div>
      )}

      {/* ── Spacing after controls ── */}
      <div style={{ height: isMobile ? '16px' : '0' }} />

      {false && tab !== 'costs' && !(isMobile && tab === 'weekly') && (
        <div style={{ padding: isMobile ? '0 16px' : '0', marginBottom:'24px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px', flexWrap:'wrap' }}>
            <span style={{ fontSize:'13px', fontWeight:700, color:'#0f172a' }}>{MONTHS[cmIdx]} {year}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', padding:'3px 10px', borderRadius:'20px', background:'#fef9c3', border:'1px solid #fde047', fontSize:'11px', fontWeight:700, color:'#854d0e', textTransform:'uppercase', letterSpacing:'0.04em' }}>Month to Date</span>
            {proRateLabel && (
              <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', padding:'3px 10px', borderRadius:'20px', background:'#f0f9ff', border:'1px solid #bae6fd', fontSize:'11px', fontWeight:600, color:'#0369a1' }}>
                ⏱ {proRateDays}/{proRateTotalDays} days · Budget &amp; LY pro-rated to {proRateLabel?.split('(')[0].trim()}
              </span>
            )}
          </div>
          {isMobile ? (
            /* Mobile: all 3 metrics in one compact card */
            <div style={{ background:'#fff', borderRadius:'12px', padding:'14px 16px', border:'1px solid #e2e8f0', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ display:'flex', gap:'12px' }}>
                {summaryCards.map((card, i) => (
                  <>
                    {i > 0 && <div key={`div-${i}`} style={{ width:'1px', background:'#f1f5f9', flexShrink:0 }}/>}
                    <SummaryCard key={card.label} {...card} isMobile={true}/>
                  </>
                ))}
              </div>
              {proRateLabel && (
                <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'10px', fontStyle:'italic', borderTop:'1px solid #f1f5f9', paddingTop:'8px' }}>
                  ⏱ Pro-rated to {proRateDays}/{proRateTotalDays} days
                </div>
              )}
            </div>
          ) : (
            <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
              {summaryCards.map(card=><SummaryCard key={card.label} {...card} isMobile={false}/>)}
            </div>
          )}
        </div>
      )}

      {/* ── WEEKLY TAB ── */}
      {tab==='weekly' && (
        loading ? (
          <div style={{ textAlign:'center', padding:'60px', color:'#94a3b8' }}>Loading...</div>
        ) : enriched.length===0 ? (
          <div style={{ margin: isMobile ? '0 16px' : '0', textAlign:'center', padding:'60px', color:'#94a3b8', background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0' }}>No results yet for {venue} in {year}.</div>
        ) : (
          <>
          {modalRow && (() => {
            const lyRow = enrichedLY.find(ly => ly.week === modalRow.week);
            return (
              <WeekModal
                r={modalRow} lyRow={lyRow} dailyData={dailyData} dailyDataLY={dailyDataLY}
                enriched={enriched} enrichedLY={enrichedLY}
                venue={venue} year={year}
                comments={comments}
                isManager={isManager}
                newComment={newComment} setNewComment={setNewComment}
                postingComment={postingComment} onPostComment={handlePostComment}
                views={views} onView={handleView} currentUser={currentUser}
                onNavigate={(week) => setModalRow(week)}
                onClose={() => setModalRow(null)}
              />
            );
          })()}

          {isMobile ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              {/* Mobile revenue trend — last 16 completed weeks, no dots */}
              {chartData16.length > 1 && (
                <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'14px 16px', margin:'0 16px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                    <span style={{ fontWeight:700, fontSize:'13px', color:'#0f172a' }}>Revenue Trend</span>
                    <div style={{ display:'flex', gap:'10px' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'#64748b' }}><span style={{ width:14, height:2, background:'#3A8FA3', display:'inline-block', borderRadius:2 }}/>{year}</span>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'#64748b' }}><span style={{ width:14, height:2, background:'#7A8A96', display:'inline-block', borderRadius:2 }}/>{year-1}</span>
                    </div>
                  </div>
                  <ResponsiveContainer width='100%' height={140}>
                    <LineChart data={chartData16} margin={{ top:4, right:8, left:0, bottom:0 }}>
                      <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9'/>
                      <XAxis dataKey='week' tick={{fontSize:9, fill:'#94a3b8'}} interval='preserveStartEnd'/>
                      <YAxis tick={{fontSize:10, fill:'#94a3b8'}} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`} width={48}/>
                      <Tooltip formatter={fmt} labelStyle={{fontSize:'11px'}} contentStyle={{fontSize:'12px'}}/>
                      <Line type='monotone' dataKey='Revenue' stroke='#3A8FA3' strokeWidth={2.5} dot={false} connectNulls/>
                      <Line type='monotone' dataKey='Revenue LY' stroke='#7A8A96' strokeWidth={1.5} strokeDasharray='4 2' dot={false} connectNulls/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Mobile Hours / Productivity toggle */}
              {chartData16.some(d => d['Hours'] != null || d['Hours LY'] != null) && (
                <MobileHoursToggle chartData16={chartData16} year={year}/>
              )}

              {/* Mobile weekly table */}
              <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', overflow:'hidden', margin:'0 16px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ padding:'10px 14px', borderBottom:'1px solid #f1f5f9', background:'#f8fafc', display:'flex', flexWrap:'wrap', gap:'5px', alignItems:'center' }}>
                <button onClick={()=>setSelectedMonths(null)} style={{ padding:'3px 10px', borderRadius:'20px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:500, background:selectedMonths===null?'#1e293b':'#e2e8f0', color:selectedMonths===null?'#fff':'#64748b' }}>All</button>
                {availableMonths.map(m=>{
                  const isActive=selectedMonths!==null&&selectedMonths.includes(m);
                  return <button key={m} onClick={()=>toggleMonth(m)} style={{ padding:'3px 10px', borderRadius:'20px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:500, background:isActive?'#1e293b':'#e2e8f0', color:isActive?'#fff':'#64748b' }}>{MONTHS[m-1]}</button>;
                })}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 14px', background:'#f8fafc', borderBottom:'1px solid #f1f5f9' }}>
                <span style={{ fontSize:'10px', fontWeight:700, color:'#94a3b8', textTransform:'uppercase' }}>Week</span>
                <span style={{ fontSize:'10px', fontWeight:700, color:'#94a3b8', textTransform:'uppercase' }}>Revenue · vs LY  ›</span>
              </div>
              {tableWeeks.length === 0
                ? <div style={{ padding:'24px', textAlign:'center', color:'#94a3b8', fontSize:'13px' }}>No data for selected period</div>
                : tableWeeks.map((r, idx) => {
                    const lyRow = enrichedLY.find(ly => ly.week === r.week);
                    const monthLabel = MONTHS[weekToMonthIndex(r.week, r.year)];
                    const nextR = tableWeeks[idx + 1];
                    const showMonthLabel = selectedMonths === null && (nextR === undefined || weekToMonthIndex(r.week, r.year) !== weekToMonthIndex(nextR.week, nextR.year));
                    return <MobileWeekRow key={r.id||r.week} r={r} lyRow={lyRow} monthLabel={monthLabel} showMonthLabel={showMonthLabel} onClick={() => setModalRow(r)} weekRank={getWeekRank(r.total_revenue)}/>;
                  })
              }
            </div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1.2fr', gap:'24px', alignItems:'start' }}>
              <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ padding:'10px 14px', borderBottom:'1px solid #f1f5f9', background:'#f8fafc', display:'flex', flexWrap:'wrap', gap:'5px', alignItems:'center' }}>
                  <button onClick={()=>setSelectedMonths(null)} style={{ padding:'3px 10px', borderRadius:'20px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:500, background:selectedMonths===null?'#1e293b':'#e2e8f0', color:selectedMonths===null?'#fff':'#64748b', transition:'all 0.15s' }}>All</button>
                  {availableMonths.map(m=>{
                    const isActive=selectedMonths!==null&&selectedMonths.includes(m);
                    return <button key={m} onClick={()=>toggleMonth(m)} style={{ padding:'3px 10px', borderRadius:'20px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:500, background:isActive?'#1e293b':'#e2e8f0', color:isActive?'#fff':'#64748b', transition:'all 0.15s' }}>{MONTHS[m-1]}</button>;
                  })}
                </div>
                <div style={{ padding:'12px 16px', borderBottom:'1px solid #f1f5f9', fontWeight:700, fontSize:'14px', color:'#0f172a' }}>
                  {selectedMonths===null?`All weeks — ${venue} ${year}`:selectedMonths.length===1?`${MONTHS[selectedMonths[0]-1]} ${year} — ${venue}`:`${selectedMonths.map(m=>MONTHS[m-1]).join(', ')} ${year} — ${venue}`}
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom:'1px solid #f1f5f9', background:'#f8fafc' }}>
                        {['Week','Revenue','vs LY','Covers','Rev/Hr','Hours'].map((h,i)=>(
                          <th key={i} style={{ padding:'10px 12px', textAlign:h==='Week'?'center':'right', fontSize:'11px', fontWeight:700, color:'#64748b', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableWeeks.length===0
                        ? <tr><td colSpan={6} style={{ padding:'24px', textAlign:'center', color:'#94a3b8', fontSize:'13px' }}>No data for selected period</td></tr>
                        : tableWeeks.map((r,idx) => {
                          const lyRow=enrichedLY.find(ly=>ly.week===r.week);
                          const monthLabel=MONTHS[weekToMonthIndex(r.week,r.year)];
                          const nextR=tableWeeks[idx+1];
                          const showDivider=selectedMonths===null&&nextR!==undefined&&weekToMonthIndex(r.week,r.year)!==weekToMonthIndex(nextR.week,nextR.year);
                          const lyRevDiff=lyRow?r.total_revenue-lyRow.total_revenue:null;
                          const lyRevPct=lyRow?pct(r.total_revenue,lyRow.total_revenue):null;
                          const lyAhead=lyRevDiff!==null&&lyRevDiff>=0;
                          const rowRevHr=r.total_hours>0?Math.round(r.total_revenue/r.total_hours):null;
                          const lyRevHr=lyRow?.total_hours>0?Math.round(lyRow.total_revenue/lyRow.total_hours):null;
                          const revHrDiff=rowRevHr&&lyRevHr?rowRevHr-lyRevHr:null;
                          const revHrAhead=revHrDiff!==null?revHrDiff>=0:null;
                          const rpc=r.total_covers>0?Math.round((r._avg_rev_source??r.total_revenue)/r.total_covers):null;
                          const weekRank = getWeekRank(r.total_revenue);
                          const isTop3 = weekRank !== null;
                          return (
                            <>
                              {(idx===0||showDivider) && <tr key={`div-${r.week}`}><td colSpan={6} style={{ padding:'4px 12px', background:'#f1f5f9', fontSize:'10px', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>{MONTHS[weekToMonthIndex(r.week,r.year)]}</td></tr>}
                              <tr key={r.id||r.week} style={{ borderBottom:'1px solid #f8fafc', background: isTop3 ? '#f0fdf4' : idx%2===0?'#fff':'#fafafa', cursor:'pointer' }} onClick={()=>setModalRow(r)}>
                                <td style={{ padding:'10px 12px', textAlign:'center' }}>
                                  <div style={{ fontWeight:700, fontSize:'13px', color: isTop3 ? '#15803d' : '#0f172a' }}>W{r.week}</div>
                                  {isTop3 && <div style={{ fontSize:'9px', fontWeight:800, color:'#15803d', marginTop:'1px' }}>🍋{weekRank}</div>}
                                </td>
                                <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, fontSize:'13px', color:'#0f172a' }}>{fmt(r.total_revenue)}</td>
                                <td style={{ padding:'10px 12px', textAlign:'right' }}>
                                  {lyRevDiff!==null?(
                                    <div>
                                      <div style={{ fontSize:'12px', fontWeight:700, color:lyAhead?'#10b981':'#ef4444' }}>{lyAhead?'+':''}{fmt(Math.round(lyRevDiff))}</div>
                                      <div style={{ fontSize:'10px', color:lyAhead?'#10b981':'#ef4444', fontWeight:500 }}>{lyAhead?'▲':'▼'} {Math.abs(lyRevPct)}%</div>
                                    </div>
                                  ):<span style={{ fontSize:'11px', color:'#cbd5e1' }}>—</span>}
                                </td>
                                <td style={{ padding:'10px 12px', textAlign:'right', fontSize:'12px' }}>
                                  <div style={{ fontWeight:600, color:'#0f172a' }}>{fmtNum(r.total_covers)||'—'}</div>
                                  {rpc && <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'1px' }}>{fmt(rpc)}/cov</div>}
                                </td>
                                <td style={{ padding:'10px 12px', textAlign:'right', fontSize:'12px' }}>
                                  {rowRevHr ? (
                                    <div>
                                      <div style={{ fontWeight:700, color:'#0f172a' }}>{fmt(rowRevHr)}</div>
                                      {revHrDiff!==null && <div style={{ fontSize:'10px', color:revHrAhead?'#10b981':'#ef4444', fontWeight:600, marginTop:'1px' }}>{revHrAhead?'▲':'▼'} {fmt(Math.abs(revHrDiff))}</div>}
                                    </div>
                                  ) : <span style={{ color:'#cbd5e1' }}>—</span>}
                                </td>
                                <td style={{ padding:'10px 12px', textAlign:'right', fontSize:'12px', color:'#64748b' }}>
                                  {r.total_hours > 0 ? (
                                    <div>
                                      <div style={{ fontWeight:600, color:'#374151' }}>{Math.round(r.total_hours)}h</div>
                                      {(r.kitchen_hours > 0 || r.foh_hours > 0) && (
                                        <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'1px' }}>
                                          K:{Math.round(r.kitchen_hours||0)} F:{Math.round(r.foh_hours||0)}
                                        </div>
                                      )}
                                    </div>
                                  ) : '—'}
                                </td>

                              </tr>
                            </>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>

              <WeeklyChartsPanel
                chartData={chartData} chartData8={chartData8}
                year={year} isGroup={isGroup}
              />
            </div>
          )}
          </>
        )
      )}

      {/* ── MONTHLY TAB ── */}
      {tab==='monthly' && (
        <MonthlyTab
          monthly={monthly} monthlyLY={monthlyLY}
          monthlyChartData={monthlyChartData}
          dailyData={dailyData} dailyDataLY={dailyDataLY}
          venue={venue} year={year} isMobile={isMobile}
        />
      )}

      {/* ── COSTS TAB ── */}
      {tab==='costs' && (
        <div style={{ padding: isMobile ? '0 16px' : '0' }}>
          <CostsTab monthly={monthly} monthlyLY={monthlyLY} year={year} venue={venue} isMobile={isMobile} externalSubTab={costsSubTab} setExternalSubTab={setCostsSubTab}/>
        </div>
      )}

    </div>
  );
}